import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreAddOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SnippetsOutlined,
  MoreOutlined,
  RightOutlined,
  DownOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  createAdminFormGroup,
  createCustomForm,
  deleteAdminFormGroup,
  deleteCustomForm,
  getAdminClients,
  getAdminFormGroups,
  getAvailableModules,
  getCustomForms,
  getModuleFields,
  getModuleDefaultFields,
  getLookupRecordOptions,
  updateAdminFormGroup,
  updateCustomForm,
} from '../../../services/adminApi';
import { normalizeModuleOptions } from '../../../utils/moduleOptions';
import AddFormV1 from '../../../components/AddFormV1';
import AppCard from '../../../components/cards/AppCard';
import { INPUT_VALIDATOR_TYPES } from '../../../components/form/inputValidator';
import { UPLOAD_ACCEPT_OPTIONS } from '../../../components/form/uploadAccept';
import OtpGateModal from '../../../components/admin/OtpGateModal';
import DefaultFieldsPanel from './components/DefaultFieldsPanel';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

// OTP-gated config actions surfaced as buttons next to "New Group". Each opens
// the OtpGateModal which mails a code and performs the action on verification.
const CONFIG_GATE_META = {
  reset:   { action: 'reset',   title: 'Reset all form groups',    confirmLabel: 'Reset All',       danger: true,
    description: 'This reseeds every module in this workspace back to the built-in default groups, discarding current customizations.' },
  save:    { action: 'save',    title: 'Save current as default',  confirmLabel: 'Save Default',    danger: false,
    description: 'Snapshots the current form groups for all modules as the saved default you can restore to later.' },
  restore: { action: 'restore', title: 'Restore saved default',    confirmLabel: 'Restore Default', danger: true,
    description: 'Replaces the current form groups for all modules with the last saved default.' },
};

const TAB_COLORS = ['#0053a5', '#52c41a', '#722ed1', '#fa8c16', '#eb2f96', '#13c2c2'];

const FIELD_TYPES = [
  { label: 'Text',        value: 'text' },
  { label: 'Number',      value: 'number' },
  { label: 'Email',       value: 'email' },
  { label: 'Date',        value: 'date' },
  { label: 'Time Picker', value: 'time' },
  { label: 'Location',    value: 'location' },
  { label: 'Select',      value: 'select' },
  { label: 'Radio',       value: 'radio' },
  { label: 'Checkbox',    value: 'checkbox' },
  { label: 'Tagify',      value: 'tagify' },
  { label: 'Textarea',    value: 'textarea' },
  { label: 'Text Editor', value: 'text-editor' },
  { label: 'File',        value: 'file' },
];

const DROPDOWN_SOURCES = [
  { label: 'Static (define options here)', value: 'static' },
  { label: 'Masters collection',           value: 'master' },
  { label: 'Module data (distinct)',        value: 'module' },
  { label: 'Collection lookup (FK)',        value: 'lookup' },
  { label: 'Group value',                  value: 'group'  },
];

const VALIDATION_TYPES = [
  { label: 'Required',          value: 'required',        hasValue: false },
  { label: 'Min length/value',  value: 'min',             hasValue: true, valueType: 'number', placeholder: 'e.g. 2' },
  { label: 'Max length/value',  value: 'max',             hasValue: true, valueType: 'number', placeholder: 'e.g. 100' },
  { label: 'Pattern (regex)',   value: 'pattern',         hasValue: true, valueType: 'text',   placeholder: '^[A-Za-z]+$' },
  { label: 'Email (reject numeric-only address)', value: 'email', hasValue: false },
  { label: 'Phone format',      value: 'phone',           hasValue: false },
  { label: 'On or after another date field',  value: 'dateAfterField',  hasValue: true, valueType: 'text', placeholder: 'startDate', dateOnly: true, messageOptional: true },
  { label: 'On or before another date field', value: 'dateBeforeField', hasValue: true, valueType: 'text', placeholder: 'endDate', dateOnly: true, messageOptional: true },
  { label: 'Before another time field', value: 'timeBeforeField', hasValue: true, valueType: 'text', placeholder: 'endTime', messageOptional: true },
  { label: 'After another time field',  value: 'timeAfterField',  hasValue: true, valueType: 'text', placeholder: 'startTime', messageOptional: true },
  { label: 'File types allowed',value: 'fileType',        hasValue: true, valueType: 'text',   placeholder: 'pdf,doc,jpg', messageOptional: true },
  { label: 'Max file size (MB)',value: 'fileSize',        hasValue: true, valueType: 'number', placeholder: 'e.g. 5',     messageOptional: true },
  { label: 'Max files count',   value: 'maxFiles',        hasValue: true, valueType: 'number', placeholder: 'e.g. 3',     messageOptional: true },
  { label: 'Greater than or equal to field', value: 'greaterThanField',       hasValue: true, valueType: 'text', placeholder: 'experienceRequiredFrom' },
  { label: 'Greater than field (strict)',     value: 'strictGreaterThanField', hasValue: true, valueType: 'text', placeholder: 'numberOfPosition' },
  { label: 'Less than field (strict)',        value: 'strictLessThanField',    hasValue: true, valueType: 'text', placeholder: 'experienceRequiredTo' },
  { label: 'Less than or equal to field',     value: 'lessThanOrEqualField',   hasValue: true, valueType: 'text', placeholder: 'experienceRequiredTo' },
  { label: 'Max field with fallback',         value: 'maxFieldWithFallback',  hasValue: true, valueType: 'text', placeholder: 'preferredToField,fallbackFromField' },
  { label: 'Disable previous dates', value: 'noPastDate',   hasValue: false, messageOptional: true, dateOnly: true },
  { label: 'Disable future dates',   value: 'noFutureDate', hasValue: false, messageOptional: true, dateOnly: true },
  { label: 'Min age (years)',        value: 'minAge',       hasValue: true, valueType: 'number', placeholder: 'e.g. 18', messageOptional: true, dateOnly: true },
];

const SHOW_IF_OPERATORS = [
  { label: 'Equals (=)',      value: 'eq' },
  { label: 'Not equals (≠)', value: 'neq' },
  { label: 'Has any value',   value: 'truthy' },
  { label: 'Is empty',        value: 'falsy' },
  { label: 'Is one of (comma-separated)',     value: 'in' },
  { label: 'Is not one of (comma-separated)', value: 'notIn' },
  { label: 'Has at least one row (addRow group)', value: 'notEmpty' },
];
// Operators whose showIf carries a comparison value.
const SHOW_IF_VALUE_OPERATORS = ['eq', 'neq', 'in', 'notIn'];

// ── Payload-engine config option lists ──────────────────────────────────────────
// These drive the config-driven payload transformer (CORE-THEME/src/components/
// form/payloadTransformer.js). Admin controls payload shape with NO frontend code.
const DATA_TYPES = [
  { label: 'Auto (infer from field)', value: 'auto' },
  { label: 'String',  value: 'string' },
  { label: 'Number',  value: 'number' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Array',   value: 'array' },
  { label: 'Object',  value: 'object' },
  { label: 'Date (ISO)', value: 'date' },
];

const ELEMENT_TYPES = [
  { label: '(none)',  value: '' },
  { label: 'String',  value: 'string' },
  { label: 'Number',  value: 'number' },
  { label: 'Boolean', value: 'boolean' },
];

const PAYLOAD_MODES = [
  { label: 'Auto (value as-is)',            value: 'auto' },
  { label: 'Value only — { key: id }',      value: 'value' },
  { label: 'Label only — { key: "India" }', value: 'label' },
  { label: 'Full object — { id, name }',    value: 'object' },
  { label: 'Custom template',               value: 'custom' },
  { label: 'Skip (never send)',             value: 'skip' },
];

const VALIDATION_TAG_COLOR = {
  required: 'red', min: 'orange', max: 'gold', pattern: 'purple',
  email: 'blue', phone: 'cyan', dateAfterField: 'volcano', dateBeforeField: 'orange',
  fileType: 'cyan', fileSize: 'blue', maxFiles: 'geekblue',
  greaterThanField: 'lime', strictGreaterThanField: 'green', strictLessThanField: 'cyan',
  lessThanOrEqualField: 'blue', maxFieldWithFallback: 'purple',
  noPastDate: 'geekblue', noFutureDate: 'magenta', minAge: 'green',
};

function validationMeta(type) {
  return VALIDATION_TYPES.find((v) => v.value === type) ?? { hasValue: false };
}

function buildModuleTabs(apiModules) {
  const all = { key: 'all', label: 'All', color: '#8c8c8c' };
  const rest = apiModules.map((m, i) => ({
    key: m.key, label: m.label, color: TAB_COLORS[i % TAB_COLORS.length],
  }));
  return [all, ...rest];
}

function initModuleState(tabs) {
  return Object.fromEntries(tabs.map(({ key }) => [key, { groups: [], loading: false, loaded: false, error: '' }]));
}

// ── Shared helpers ────────────────────────────────────────────────────────────

// safeParseObject parses an admin-entered JSON string into an object. Returns
// undefined for blank/invalid input so the key is simply omitted (the backend
// model stores these as objects, never strings).
function safeParseObject(input) {
  if (input == null) return undefined;
  if (typeof input === 'object') return input;
  const str = String(input).trim();
  if (!str) return undefined;
  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

// stringifyObject renders a stored object config back to pretty JSON for editing.
function stringifyObject(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

// applyPayloadConfig copies the payload-engine config off a raw field-editor row
// onto the outgoing payload, omitting empty/default values so docs stay lean.
function applyPayloadConfig(fp, f) {
  if (f.payloadKey)  fp.payloadKey  = f.payloadKey;
  if (f.prefillFrom) fp.prefillFrom = f.prefillFrom;
  if (f.prefillFromModule) fp.prefillFromModule = f.prefillFromModule;
  const prefillWhenRows = (Array.isArray(f.prefillWhen) ? f.prefillWhen : [])
    .filter((r) => r && r.field && r.value !== undefined && r.value !== '' && r.setValue !== undefined && r.setValue !== '');
  if (prefillWhenRows.length) fp.prefillWhen = prefillWhenRows;
  if (f.dataType && f.dataType !== 'auto')       fp.dataType = f.dataType;
  if (f.elementType)                              fp.elementType = f.elementType;
  if (f.payloadMode && f.payloadMode !== 'auto') fp.payloadMode = f.payloadMode;
  if (f.valueKey)    fp.valueKey    = f.valueKey;
  if (f.displayKey)  fp.displayKey  = f.displayKey;
  if (f.defaultValue !== undefined && f.defaultValue !== '') fp.defaultValue = f.defaultValue;
  if (f.richValue)   fp.richValue   = true;
  if (f.omitEmpty)   fp.omitEmpty   = true;
  const tpl = safeParseObject(f.payloadTemplate);
  if (tpl) fp.payloadTemplate = tpl;
  const tr = safeParseObject(f.transformRule);
  if (tr) fp.transformRule = tr;
  return fp;
}

function buildFieldPayload(fields = []) {
  // A row with a blank field key would save an unrenderable field — skip it.
  return fields.filter((f) => String(f?.field ?? '').trim() !== '').map((f, idx) => {
    const validations = (f.validations ?? [])
      .filter((v) => v?.type)
      .map((v) => {
        const meta = validationMeta(v.type);
        const rule = { type: v.type, message: v.message ?? '' };
        if (meta.hasValue && v.value !== '' && v.value !== undefined && v.value !== null) {
          rule.value = meta.valueType === 'number' ? Number(v.value) : v.value;
        }
        return rule;
      });

    const fp = {
      label: f.label, field: f.field, type: f.type ?? 'text',
      show: f.show ? 1 : 0, req: f.req ? 1 : 0, edit: f.edit ? 1 : 0,
      // Persist the field's position so the form renders in the SAME order the
      // admin arranged by drag-and-drop. Both forms sort fields by `order`, so
      // without this a reorder in the editor never reached the rendered form.
      order: idx,
      formatter: f.formatter ?? '', validations,
    };
    if (f.showOnAdd === false) fp.showOnAdd = false;
    if (f.addRow) {
      fp.addRow = true;
      if (f.minRows !== undefined && f.minRows !== '' && Number(f.minRows) > 0) fp.minRows = Number(f.minRows);
      if (f.maxRows !== undefined && f.maxRows !== '' && Number(f.maxRows) > 0) fp.maxRows = Number(f.maxRows);
      if (f.initialRows !== undefined && f.initialRows !== '') fp.initialRows = Number(f.initialRows);
      if (f.addLabel) fp.addLabel = f.addLabel;
      // Sub-fields → repeat a GROUP of fields together (e.g. question + answer).
      // Only rows with a key are kept.
      const subs = (f.subFields ?? []).filter((s) => s && s.field);
      if (subs.length) {
        fp.subFields = subs.map((s) => {
          // show:1 / edit:1 are REQUIRED — the Go FormGroupField serializes
          // show/edit as 0 when absent (no omitempty), and the form engine reads
          // edit:0 as "not editable", disabling the control. Sub-fields have no
          // show/edit admin toggle, so they are always visible + editable.
          const sp = {
            field: String(s.field).trim(),
            label: s.label ?? s.field,
            type: s.type ?? 'text',
            show: 1,
            edit: 1,
            req: s.req ? 1 : 0,
          };
          if (s.col !== undefined && s.col !== '' && Number(s.col) > 0) sp.col = Number(s.col);
          if (s.payloadKey) sp.payloadKey = String(s.payloadKey).trim();
          // A sub-field of type select/radio/checkbox needs the SAME dataSource
          // config as a normal field, or its control renders with no options —
          // it uses the same FormGroupField struct, so every key here reaches
          // the runtime FieldControl unchanged.
          if (['select', 'radio', 'checkbox'].includes(sp.type)) {
            const sds = s.dataSource ?? 'static';
            sp.dataSource = sds;
            if (sp.type === 'select') sp.multiSelect = s.multiSelect ?? false;
            if (sds === 'master') sp.masterName = s.masterName ?? '';
            if (sds === 'group') sp.groupName = s.groupName ?? '';
            if (sds === 'static') sp.options = (s.options ?? []).filter((o) => o?.label || o?.value);
            if (sds === 'lookup') {
              sp.lookupCollection = s.lookupCollection ?? '';
              sp.displayField = s.displayField ?? '';
              if (s.displayField2) sp.displayField2 = s.displayField2;
              sp.valueField = s.valueField ?? '_id';
              if (s.lookupFilterField && s.lookupFilterValueFrom) {
                sp.lookupFilterField = s.lookupFilterField;
                sp.lookupFilterValueFrom = s.lookupFilterValueFrom;
              }
            }
          }
          return sp;
        });
      }
    }
    if (f.placeholder) fp.placeholder = f.placeholder;
    if (f.hidePlaceholder) fp.hidePlaceholder = true;

    if (f.combineKey) fp.combineKey = f.combineKey;
    if (f.combineLabel) fp.combineLabel = f.combineLabel;
    if (f.combineRole) fp.combineRole = f.combineRole;
    if (f.combineToggleLabel) fp.combineToggleLabel = f.combineToggleLabel;
    if (f.combineToggleTarget) fp.combineToggleTarget = f.combineToggleTarget;
    if (f.combineWidth !== undefined && f.combineWidth !== '' && Number(f.combineWidth) > 0) fp.combineWidth = Number(f.combineWidth);
    if (f.combineWrap) fp.combineWrap = true;
    if (f.combineSpan !== undefined && f.combineSpan !== '' && [12, 24].includes(Number(f.combineSpan))) fp.combineSpan = Number(f.combineSpan);
    if (f.defaultOnEdit) fp.defaultOnEdit = true;
    if (f.emptyValues !== undefined && f.emptyValues !== '') {
      fp.emptyValues = String(f.emptyValues).split(',').map((v) => v.trim()).filter((v) => v !== '');
    }
    if (f.valuePaths !== undefined && f.valuePaths !== '') {
      fp.valuePaths = String(f.valuePaths).split(',').map((v) => v.trim()).filter((v) => v !== '');
    }
    if (f.clientScope) {
      fp.clientScope = true;
      if (f.clientGateMessage) fp.clientGateMessage = f.clientGateMessage;
      if (f.clientGateResetOnChange) fp.clientGateResetOnChange = true;
    }
    if (f.visiblePermission) fp.visiblePermission = String(f.visiblePermission).trim();
    if (f.editablePermission) fp.editablePermission = String(f.editablePermission).trim();
    if (f.infoEnabled && f.infoText) {
      fp.infoEnabled = true;
      fp.infoText = f.infoText;
    }
    if (f.helpText) fp.helpText = f.helpText;

    // Payload-engine config (payloadKey / dataType / payloadMode / template / …)
    applyPayloadConfig(fp, f);

    if (f.type === 'file') {
      fp.multiple = f.multiple ?? false;
      fp.maxCount = fp.multiple ? Number(f.maxCount ?? 5) : 1;
      if (f.fileKey) fp.fileKey = f.fileKey;
      if (f.fileNameKey) fp.fileNameKey = f.fileNameKey;
      if (f.fileUrlKey) fp.fileUrlKey = f.fileUrlKey;
      if (f.accept && f.accept !== 'any') fp.accept = f.accept;
    }

    if (f.showIf?.field) {
      fp.showIf = { field: f.showIf.field, operator: f.showIf.operator ?? 'eq' };
      const op = fp.showIf.operator;
      if (SHOW_IF_VALUE_OPERATORS.includes(op) && f.showIf.value != null && f.showIf.value !== '') {
        fp.showIf.value = f.showIf.value;
      }
    }
    if (f.labelWhen?.field && f.labelWhen?.label) {
      fp.labelWhen = {
        field: String(f.labelWhen.field).trim(),
        operator: f.labelWhen.operator ?? 'eq',
        label: String(f.labelWhen.label).trim(),
      };
      if (SHOW_IF_VALUE_OPERATORS.includes(fp.labelWhen.operator)
          && f.labelWhen.value != null && f.labelWhen.value !== '') {
        fp.labelWhen.value = f.labelWhen.value;
      }
    }
    if (f.verifyAction) {
      fp.verifyAction = f.verifyAction;
      if (f.verifyResultField) fp.verifyResultField = String(f.verifyResultField).trim();
      if (f.verifyTimestampField) fp.verifyTimestampField = String(f.verifyTimestampField).trim();
      if (f.verifyIdleLabel) fp.verifyIdleLabel = String(f.verifyIdleLabel).trim();
      if (f.verifyRetryLabel) fp.verifyRetryLabel = String(f.verifyRetryLabel).trim();
      if (f.verifySuccessLabel) fp.verifySuccessLabel = String(f.verifySuccessLabel).trim();
    }
    if (f.disabledByField) fp.disabledByField = String(f.disabledByField).trim();
    if (f.linkedClearField) fp.linkedClearField = String(f.linkedClearField).trim();
    if (f.autofillFrom) {
      try {
        const parsed = typeof f.autofillFrom === 'string' ? JSON.parse(f.autofillFrom) : f.autofillFrom;
        if (Array.isArray(parsed) && parsed.length > 0) fp.autofillFrom = parsed;
      } catch { /* ignore invalid JSON */ }
    }
    if (Array.isArray(f.aiActions) && f.aiActions.length > 0) {
      fp.aiActions = f.aiActions
        .filter((a) => a?.key && a?.label && a?.serviceKey && a?.endpointPath)
        .map((a) => ({
          key: a.key, label: a.label, trigger: a.trigger || 'click',
          serviceKey: a.serviceKey, endpointPath: a.endpointPath, bodyType: a.bodyType || 'json',
          inputs: (a.inputs || []).filter((i) => i?.param && i?.sourceField).map((i) => ({
            param: i.param, sourceField: i.sourceField, kind: i.kind || 'text',
            ...(i.transform ? { transform: i.transform } : {}),
            ...(i.group ? { group: i.group } : {}),
            ...(i.required ? { required: true } : {}),
            ...(Number(i.minWords) > 0 ? { minWords: Number(i.minWords) } : {}),
          })),
          ...(String(a.responseDataPath || '').trim() ? { responseDataPath: String(a.responseDataPath).trim() } : {}),
          ...(String(a.position || '').trim() ? { position: String(a.position).trim() } : {}),
        }));
    }

    // Input validator — real-time character restriction from the DB config
    const validatorType = f.validator?.type;
    if (validatorType) {
      fp.validator = { type: validatorType };
      if (f.validator.title) fp.validator.title = f.validator.title;
      const ml = Number(f.validator.maxLength);
      if (!isNaN(ml) && ml > 0) fp.validator.maxLength = ml;
      const mc = Number(f.validator.maxChars);
      if (!isNaN(mc) && mc > 0) fp.validator.maxChars = mc;
    }

    if (['select', 'radio', 'checkbox'].includes(f.type)) {
      const ds = f.dataSource ?? 'static';
      fp.dataSource = ds;
      if (f.type === 'select') fp.multiSelect = f.multiSelect ?? false;
      if (ds === 'master')  fp.masterName       = f.masterName       ?? '';
      if (ds === 'group')   fp.groupName        = f.groupName        ?? '';
      if (ds === 'static')  fp.options          = (f.options ?? []).filter((o) => o?.label || o?.value);
      if (ds === 'lookup') {
        fp.lookupCollection = f.lookupCollection ?? '';
        fp.displayField     = f.displayField     ?? '';
        if (f.displayField2) fp.displayField2 = f.displayField2;
        fp.valueField       = f.valueField ?? '_id';
        if (f.lookupFilterField && f.lookupFilterValueFrom) {
          fp.lookupFilterField = f.lookupFilterField;
          fp.lookupFilterValueFrom = f.lookupFilterValueFrom;
        }
      }
    }

    // Preserve every config key this editor doesn't manage — verifyAction /
    // verifyResultField / verifyTimestampField, prefillWhen, disabled,
    // optionsRequireExtraField, extraFields, col, checkboxLabel, and anything
    // added by future tools/seeds. Without this, saving a group from this
    // screen silently DESTROYED config authored elsewhere. Keys the editor
    // owns (already written above, or intentionally clearable) are excluded
    // so clearing them in the UI still clears them in the DB.
    for (const [k, v] of Object.entries(f)) {
      if (v === undefined || v === null || v === '') continue;
      if (k in fp || FIELD_EDITOR_MANAGED_KEYS.has(k)) continue;
      fp[k] = v;
    }
    return fp;
  });
}

// Keys the field editor UI can set OR clear. Their absence from the built
// payload means "cleared by the admin" — never pass them through from the
// stored field. Everything NOT listed here survives a save untouched.

// Record picker for the field editor's "Conditional Default": lists real
// records from the field's own lookup collection (e.g. employers) so the
// admin selects the default by NAME instead of pasting a record id. The
// chosen option's label is kept alongside the id (setValueLabel) so the form
// can render the default before the lookup resolves.
function LookupRecordSelect({ collection, displayField, valueField, value, labelValue, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef('');

  const load = () => {
    const key = `${collection}|${displayField}|${valueField}`;
    if (!collection || !displayField || loadedRef.current === key) return;
    setLoading(true);
    getLookupRecordOptions(collection, displayField, valueField || '_id')
      .then((opts) => { loadedRef.current = key; setOptions(opts); })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  };

  const merged = value && !options.some((o) => o.value === value)
    ? [{ label: labelValue || value, value }, ...options]
    : options;

  return (
    <Select
      size="small"
      showSearch
      allowClear
      loading={loading}
      placeholder={collection ? `Pick from ${collection}` : 'Configure a lookup first'}
      disabled={!collection || !displayField}
      style={{ width: 220 }}
      optionFilterProp="label"
      options={merged}
      value={value || undefined}
      onDropdownVisibleChange={(open) => { if (open) load(); }}
      onChange={(v, option) => onChange(v ?? '', option?.label ?? '')}
      getPopupContainer={() => document.body}
    />
  );
}

const FIELD_EDITOR_MANAGED_KEYS = new Set([
  'label', 'field', 'type', 'show', 'req', 'edit', 'order', 'formatter',
  'validations', 'showOnAdd', 'addRow', 'minRows', 'maxRows', 'initialRows',
  'addLabel', 'subFields', 'placeholder', 'hidePlaceholder', 'combineKey',
  'combineLabel', 'combineRole', 'combineToggleLabel', 'combineToggleTarget',
  'combineWidth', 'combineWrap', 'combineSpan', 'defaultOnEdit', 'emptyValues',
  'valuePaths', 'clientScope', 'clientGateMessage', 'clientGateResetOnChange',
  'visiblePermission', 'editablePermission', 'infoEnabled', 'infoText',
  'helpText', 'payloadKey', 'prefillFrom', 'prefillFromModule', 'dataType', 'elementType',
  'payloadMode', 'valueKey', 'displayKey', 'defaultValue', 'richValue',
  'omitEmpty', 'payloadTemplate', 'transformRule', 'multiple', 'maxCount',
  'fileKey', 'fileNameKey', 'fileUrlKey', 'accept', 'showIf', 'labelWhen',
  'verifyAction', 'verifyResultField', 'verifyTimestampField', 'prefillWhen',
  'verifyIdleLabel', 'verifyRetryLabel', 'verifySuccessLabel',
  'disabledByField', 'linkedClearField', 'autofillFrom', 'aiActions',
  'validator', 'dataSource', 'masterName', 'groupName', 'options',
  'multiSelect', 'lookupCollection', 'displayField', 'displayField2',
  'valueField', 'lookupFilterField', 'lookupFilterValueFrom',
  // Runtime-only keys the GET response may carry — never persist them.
  'value', 'documentPreview', 'displayAs', 'renderType', 'renderOptions',
]);

// Same idea at GROUP level: keys the group editor sets/clears itself, plus
// runtime-only keys from the GET response. Anything else on a stored group
// (payloadKey, sharedRowLabels, showIf, writeRefs variants, …) passes through a
// save untouched. linkField/linkTypeField/parentRefField ARE managed here (the
// Linkage section owns them) so switching mode clears the other mode's key.
const GROUP_EDITOR_MANAGED_KEYS = new Set([
  'module', 'name', 'label', 'addRow', 'order', 'fields', 'hideGroup',
  'moduleName', 'linkField', 'linkTypeField', 'parentRefField',
  'writeRefs', 'dependsOn', 'optionalWrite', 'appendHistory',
  'historyField', 'historyFields', 'historyTimestampField', 'minRows', 'maxRows', 'initialRows',
  'requireOne', 'requireValidatedField', 'linkGroup', 'infoEnabled',
  'infoText', 'toggleEnabled', 'toggleLabel', 'toggleField', 'afterSubmit',
  'showIf', 'showIfField', 'showIfOperator', 'showIfValue',
  'formActions',
  'afterSubmitMode', 'afterSubmitTarget', 'submitMessages',
  'submitMsgAddSuccess', 'submitMsgEditSuccess', 'submitMsgAddError',
  'submitMsgEditError', 'submitMsgSuppress',
  // Runtime-only / identity keys — never persist into the groups array.
  'id', '_id', 'rows', 'toggleValue',
]);

function initFieldValues(f) {
  return {
    ...f,
    show: f.show === 1, req: f.req === 1, edit: f.edit === 1,
    showOnAdd: f.showOnAdd !== false,
    addRow: f.addRow ?? false,
    minRows: f.minRows ?? '',
    maxRows: f.maxRows ?? '',
    initialRows: f.initialRows ?? '',
    addLabel: f.addLabel ?? '',
    subFields: Array.isArray(f.subFields) ? f.subFields.map((s) => ({
      field: s.field ?? '',
      label: s.label ?? '',
      type: s.type ?? 'text',
      req: s.req === 1 || s.req === true,
      col: s.col ?? '',
      payloadKey: s.payloadKey ?? '',
      dataSource: s.dataSource ?? 'static',
      multiSelect: s.multiSelect ?? false,
      masterName: s.masterName ?? '',
      groupName: s.groupName ?? '',
      options: Array.isArray(s.options) ? s.options : [],
      lookupCollection: s.lookupCollection ?? '',
      displayField: s.displayField ?? '',
      displayField2: s.displayField2 ?? '',
      valueField: s.valueField ?? '_id',
      lookupFilterField: s.lookupFilterField ?? '',
      lookupFilterValueFrom: s.lookupFilterValueFrom ?? '',
    })) : [],
    placeholder: f.placeholder ?? '',
    hidePlaceholder: f.hidePlaceholder ?? false,
    clientScope:        f.clientScope        ?? false,
    clientGateMessage:  f.clientGateMessage  ?? '',
    clientGateResetOnChange: f.clientGateResetOnChange ?? false,
    visiblePermission:  f.visiblePermission  ?? '',
    editablePermission: f.editablePermission ?? '',
    infoEnabled:        f.infoEnabled        ?? false,
    infoText:         f.infoText         ?? '',
    helpText:         f.helpText         ?? '',
    formatter:        f.formatter        ?? '',
    combineKey:       f.combineKey       ?? '',
    combineLabel:     f.combineLabel     ?? '',
    combineRole:      f.combineRole      ?? '',
    combineToggleLabel:  f.combineToggleLabel  ?? '',
    combineToggleTarget: f.combineToggleTarget ?? '',
    combineWidth:        f.combineWidth        ?? '',
    combineWrap:         f.combineWrap         ?? false,
    combineSpan:         f.combineSpan         ?? '',
    defaultOnEdit:       f.defaultOnEdit       ?? false,
    emptyValues:         Array.isArray(f.emptyValues) ? f.emptyValues.join(', ') : (f.emptyValues ?? ''),
    valuePaths:          Array.isArray(f.valuePaths) ? f.valuePaths.join(', ') : (f.valuePaths ?? ''),
    dataSource:       f.dataSource       ?? 'static',
    masterName:       f.masterName       ?? '',
    groupName:        f.groupName        ?? '', 
    options:          f.options          ?? [],
    lookupCollection: f.lookupCollection ?? '',
    displayField:     f.displayField     ?? '',
    displayField2:    f.displayField2    ?? '',
    valueField:       f.valueField       ?? '_id',
    lookupFilterField:     f.lookupFilterField     ?? '',
    lookupFilterValueFrom: f.lookupFilterValueFrom ?? '',
    multiSelect:      f.multiSelect      ?? false,
    multiple:         f.multiple         ?? false,
    maxCount:         f.maxCount         ?? 5,
    accept:           f.accept           ?? 'any',
    fileKey:          f.fileKey          ?? '',
    fileNameKey:      f.fileNameKey      ?? '',
    fileUrlKey:       f.fileUrlKey       ?? '',
    // Payload-engine config
    payloadKey:       f.payloadKey       ?? '',
    prefillFrom:      f.prefillFrom      ?? '',
    prefillFromModule: f.prefillFromModule ?? '',
    prefillWhen:      Array.isArray(f.prefillWhen) ? f.prefillWhen : [],
    dataType:         f.dataType         ?? 'auto',
    elementType:      f.elementType      ?? '',
    payloadMode:      f.payloadMode      ?? 'auto',
    valueKey:         f.valueKey         ?? '',
    displayKey:       f.displayKey       ?? '',
    defaultValue:     f.defaultValue     ?? '',
    richValue:        f.richValue        ?? false,
    omitEmpty:        f.omitEmpty        ?? false,
    payloadTemplate:  stringifyObject(f.payloadTemplate),
    transformRule:    stringifyObject(f.transformRule),
    showIf: f.showIf?.field
      ? { field: f.showIf.field, operator: f.showIf.operator ?? 'eq', value: f.showIf.value ?? '' }
      : { field: '', operator: 'eq', value: '' },
    labelWhen: f.labelWhen?.field
      ? { field: f.labelWhen.field, operator: f.labelWhen.operator ?? 'eq', value: f.labelWhen.value ?? '', label: f.labelWhen.label ?? '' }
      : { field: '', operator: 'eq', value: '', label: '' },
    verifyAction:         f.verifyAction         ?? '',
    verifyResultField:    f.verifyResultField    ?? '',
    verifyTimestampField: f.verifyTimestampField ?? '',
    verifyIdleLabel:      f.verifyIdleLabel      ?? '',
    verifyRetryLabel:     f.verifyRetryLabel     ?? '',
    verifySuccessLabel:   f.verifySuccessLabel   ?? '',
    validations: (f.validations ?? []).map((v) => ({
      type: v.type, value: v.value ?? '', message: v.message ?? '',
    })),
    validator: {
      type:      f.validator?.type      ?? '',
      title:     f.validator?.title     ?? '',
      maxLength: f.validator?.maxLength ?? '',
      maxChars:  f.validator?.maxChars  ?? '',
    },
    disabledByField:  f.disabledByField  ?? '',
    linkedClearField: f.linkedClearField ?? '',
    autofillFrom: Array.isArray(f.autofillFrom) ? JSON.stringify(f.autofillFrom, null, 2) : (f.autofillFrom ?? ''),
    aiActions: Array.isArray(f.aiActions) ? f.aiActions.map((a) => ({
      ...a,
      inputs: (a.inputs ?? []).map((i) => ({ ...i, minWords: i.minWords ?? '' })),
      responseDataPath: a.responseDataPath ?? '',
      position: a.position ?? '',
    })) : [],
  };
}

// ── renderFieldRows ───────────────────────────────────────────────────────────
// Shared JSX helper that renders the field editor rows for one group's Form.List.
// absBase = []                for GroupDrawer  (fields live at form root)
// absBase = ['groups', gName] for CustomFormDrawer
function renderFieldRows({
  form, fieldItems, add, remove, move,
  absBase, moduleFieldOptions,
  dragIndexRef, dragOverIndex, setDragOverIndex,
  onFieldKeySelect,
  expandedFieldKeys,
  onToggleField,
  defaultFieldSet = new Set(),
}) {
  const fieldsAbs = [...absBase, 'fields'];

  return (
    <>
      {fieldItems.map(({ key, name: rowName, ...restField }, fieldIndex) => {
        const fAbs = [...fieldsAbs, rowName];
        const fieldValue = form.getFieldValue(fAbs) ?? {};
        const isCollapsed = expandedFieldKeys && !expandedFieldKeys.has(fieldIndex);
        return (
          <div
            key={key}
            draggable
            onDragStart={() => { dragIndexRef.current = fieldIndex; }}
            onDragEnd={() => { dragIndexRef.current = null; setDragOverIndex(null); }}
            onDragOver={(e) => { e.preventDefault(); setDragOverIndex(fieldIndex); }}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragIndexRef.current;
              if (from !== null && from !== fieldIndex) move(from, fieldIndex);
              dragIndexRef.current = null;
              setDragOverIndex(null);
            }}
            className={`fg-field-card${dragOverIndex === fieldIndex ? ' fg-drag-over' : ''}`}
          >
            <div className="fg-field-card-inner">
              <span className="fg-drag-handle"><HolderOutlined /></span>
              {isCollapsed ? (
                <div className="fg-field-summary">
                  <button type="button" className="fg-field-summary-main" onClick={() => onToggleField(fieldIndex)}>
                    <RightOutlined className="fg-field-summary-chevron" />
                    <span>
                      <strong>{fieldValue.label || fieldValue.field || `Field ${fieldIndex + 1}`}</strong>
                      <small>{fieldValue.field || 'Field key not set'} · {fieldValue.type || 'text'}</small>
                    </span>
                  </button>
                  <Space size={4}>
                    {fieldValue.req && <Tag color="red">Required</Tag>}
                    {fieldValue.show === false && <Tag>Hidden</Tag>}
                    <Tooltip title="Edit field settings">
                      <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onToggleField(fieldIndex)} />
                    </Tooltip>
                    <Tooltip title="Remove field">
                      <Button type="text" size="small" danger icon={<MinusCircleOutlined />} onClick={() => remove(rowName)} />
                    </Tooltip>
                  </Space>
                </div>
              ) : (
                <>
              <div className="fg-field-body">

                {expandedFieldKeys && (
                  <button type="button" className="fg-field-collapse-btn" onClick={() => onToggleField(fieldIndex)}>
                    <DownOutlined /> Collapse field
                  </button>
                )}

                <Form.Item
                  {...restField}
                  name={[rowName, 'field']}
                  label="Field"
                  style={{ marginBottom: 10 }}
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <AutoComplete
                    options={moduleFieldOptions}
                    placeholder="Search or type field key…"
                    filterOption={(input, opt) => (opt?.searchLabel ?? '').includes(input.toLowerCase())}
                    onSelect={(val) => onFieldKeySelect(val, rowName)}
                    className="fg-mono-input"
                  />
                </Form.Item>

                <div className="fg-field-meta-row">
                  <Form.Item {...restField} name={[rowName, 'label']} label="Label"
                    style={{ marginBottom: 8, flex: 2 }} rules={[{ required: true, message: 'Required' }]}>
                    <Input size="small" placeholder="Display label" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'type']} label="Type"
                    style={{ marginBottom: 8, flex: 2 }} initialValue="text">
                    <Select size="small" options={FIELD_TYPES} />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'formatter']} label="Formatter"
                    style={{ marginBottom: 8, flex: 2 }}>
                    <Input size="small" placeholder="name / phone…" />
                  </Form.Item>
                </div>

                <div className="fg-options-block">
                  <span className="fg-options-label">Layout / Combine</span>
                  <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                    <Form.Item name={[rowName, 'combineKey']} label="Combine Key"
                      style={{ marginBottom: 0 }}
                      tooltip="Fields with the same key render together as one visual block. Payload stays unchanged.">
                      <Input size="small" placeholder="e.g. client_rate" style={{ width: 150 }} className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineLabel']} label="Combined Label"
                      style={{ marginBottom: 0 }}>
                      <Input size="small" placeholder="e.g. Client Rate" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineRole']} label="Role"
                      style={{ marginBottom: 0 }}>
                      <Select size="small" style={{ width: 120 }} allowClear options={[
                        { label: 'Primary', value: 'primary' },
                        { label: 'From', value: 'from' },
                        { label: 'To', value: 'to' },
                        { label: 'Currency', value: 'currency' },
                        { label: 'Unit', value: 'unit' },
                      ]} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineToggleLabel']} label="Toggle Label"
                      style={{ marginBottom: 0 }}>
                      <Input size="small" placeholder="e.g. Range" style={{ width: 130 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineToggleTarget']} label="Toggle Target"
                      style={{ marginBottom: 0 }}
                      tooltip="Field key hidden until the combined-field slider is enabled. Example: clientRateTo.">
                      <Input size="small" placeholder="target field key" style={{ width: 150 }} className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineWidth']} label="Width"
                      style={{ marginBottom: 0 }}
                      tooltip="Width weight of this box inside the combined block. Set the same key's fields to e.g. 2 & 1 for 2:1, or 1/2/3 for 1:2:3. Blank/0 = equal share.">
                      <Input size="small" type="number" min={0} step={0.5} placeholder="1" style={{ width: 80 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineWrap']} label="Wrap" valuePropName="checked"
                      style={{ marginBottom: 0 }}
                      tooltip="Allow this combined block to wrap to another line. Useful for wider controls such as document upload rows.">
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineSpan']} label="Block Span"
                      style={{ marginBottom: 0 }}
                      tooltip="How much row width this combined block uses. Blank keeps the existing automatic layout.">
                      <Select size="small" allowClear placeholder="Auto" style={{ width: 110 }} options={[
                        { label: 'Half', value: 12 },
                        { label: 'Full', value: 24 },
                      ]} />
                    </Form.Item>
                  </div>
                </div>

                {/* File options */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (getFieldValue([...fAbs, 'type']) !== 'file') return null;
                    const isMultiple = getFieldValue([...fAbs, 'multiple']);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">File Options</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'accept']} label="Allowed Content"
                            style={{ marginBottom: 0 }} initialValue="any"
                            tooltip="Restrict what this upload accepts. A file outside the selected category is rejected on upload. Overrides the 'File types allowed' validation rule.">
                            <Select size="small" style={{ width: 230 }} options={UPLOAD_ACCEPT_OPTIONS} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'multiple']} label="Upload Mode"
                            valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                            <Switch size="small" checkedChildren="Multiple" unCheckedChildren="Single" />
                          </Form.Item>
                          {isMultiple && (
                            <Form.Item name={[rowName, 'maxCount']} label="Max files"
                              style={{ marginBottom: 0 }} initialValue={5}
                              rules={[{ required: true, message: 'Required' }]}>
                              <Input size="small" type="number" min={2} placeholder="e.g. 5" style={{ width: 80 }} />
                            </Form.Item>
                          )}
                          <Form.Item name={[rowName, 'fileKey']} label="File Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Multipart field name sent to the server. Leave blank to use the field key. Set to 'file' for the jobs service.">
                            <Input size="small" placeholder="e.g. file" style={{ width: 120 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'fileNameKey']} label="Name Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Stored object key that contains the uploaded file name. Leave blank for automatic detection.">
                            <Input size="small" placeholder="e.g. passportUploadName" style={{ width: 180 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'fileUrlKey']} label="URL Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Stored object key that contains the file location/path. Leave blank for automatic detection.">
                            <Input size="small" placeholder="e.g. passportCopyLocation" style={{ width: 190 }} className="fg-mono-input" />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Options / datasource block */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    if (!['select', 'radio', 'checkbox'].includes(fType)) return null;
                    const ds = getFieldValue([...fAbs, 'dataSource']) ?? 'static';
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Options / Values</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'dataSource']} label="Source"
                            style={{ marginBottom: 0 }} initialValue="static">
                            <Select size="small" style={{ width: 190 }} options={DROPDOWN_SOURCES} />
                          </Form.Item>
                          {fType === 'select' && (
                            <Form.Item name={[rowName, 'multiSelect']} label="Multi Select"
                              valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                              <Switch size="small" checkedChildren="Multi" unCheckedChildren="Single" />
                            </Form.Item>
                          )}
                          {ds === 'master' && (
                            <Form.Item name={[rowName, 'masterName']} label="Master Name"
                              style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter master name' }]}>
                              <Input size="small" placeholder="e.g. tracker" style={{ width: 140 }} />
                            </Form.Item>
                          )}
                          {ds === 'group' && (
                            <Form.Item name={[rowName, 'groupName']} label="Group Name"
                              style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter group name' }]}>
                              <Input size="small" placeholder="e.g. users" style={{ width: 140 }} />
                            </Form.Item>
                          )}
                        </div>
                        {ds === 'static' && (
                          <div className="fg-static-options">
                            <span className="fg-static-options-hint">Label / Value pairs</span>
                            <Form.List name={[rowName, 'options']}>
                              {(optItems, { add: addOpt, remove: removeOpt }) => (
                                <>
                                  {optItems.map(({ key: oKey, name: oName }) => (
                                    <div key={oKey} className="fg-option-row">
                                      <Form.Item name={[oName, 'label']} style={{ marginBottom: 0, flex: 1 }}>
                                        <Input size="small" placeholder="Label" />
                                      </Form.Item>
                                      <Form.Item name={[oName, 'value']} style={{ marginBottom: 0, flex: 1 }}>
                                        <Input size="small" placeholder="Value" className="fg-mono-input" />
                                      </Form.Item>
                                      <MinusCircleOutlined onClick={() => removeOpt(oName)} className="fg-remove-icon" />
                                    </div>
                                  ))}
                                  <Button size="small" type="dashed" icon={<PlusOutlined />}
                                    onClick={() => addOpt({ label: '', value: '' })}
                                    className="fg-add-option-btn">
                                    Add Option
                                  </Button>
                                </>
                              )}
                            </Form.List>
                          </div>
                        )}
                        {ds === 'module' && <p className="fg-source-hint">Options fetched from the module&apos;s distinct field values at runtime.</p>}
                        {ds === 'group'  && <p className="fg-source-hint">Use &quot;users&quot; to load all users from the MongoDB-backed user collection.</p>}
                        {ds === 'lookup' && (
                          <div className="fg-lookup-block">
                            <p className="fg-source-hint" style={{ marginBottom: 8 }}>Cross-collection lookup &mdash; shows label, stores FK value.</p>
                            <div className="fg-field-meta-row">
                              <Form.Item name={[rowName, 'lookupCollection']} label="Collection"
                                style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                <Input size="small" placeholder="e.g. vendors" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'displayField']} label="Display Field"
                                style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                <Input size="small" placeholder="e.g. vendorContact.firstName" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'displayField2']} label="Display Field 2"
                                style={{ marginBottom: 0, flex: 1 }}>
                                <Input size="small" placeholder="e.g. vendorContact.lastName" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'valueField']} label="Value Field (FK)"
                                style={{ marginBottom: 0, flex: 1 }}>
                                <Input size="small" placeholder="_id" className="fg-mono-input" />
                              </Form.Item>
                            </div>
                            <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                              <Form.Item name={[rowName, 'lookupFilterField']} label="Filter Field"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Optional DB field to filter lookup rows by. Example: _id or clientId.">
                                <Input size="small" placeholder="e.g. _id" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'lookupFilterValueFrom']} label="Filter Value From"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Optional form/scope field that provides the filter value. Example: clientId.">
                                <Input size="small" placeholder="e.g. clientId" className="fg-mono-input" />
                              </Form.Item>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Show / Req / Edit. A Default Field (Form Groups → Default
                    Fields, OTP-gated) is always shown + required, so its Show
                    and Req switches are forced ON and locked here. */}
                <div className="fg-toggles-row">
                  {(() => {
                    const isDefault = defaultFieldSet.has(fieldValue.field);
                    return (
                      <>
                        <Form.Item {...restField} name={[rowName, 'show']} valuePropName="checked"
                          style={{ marginBottom: 0 }} initialValue={true}>
                          <Switch size="small" checkedChildren="Show" unCheckedChildren="Hide"
                            checked={isDefault ? true : undefined} disabled={isDefault}
                            title={isDefault ? 'Locked — Default Field (always shown)' : undefined} />
                        </Form.Item>
                        <Form.Item {...restField} name={[rowName, 'req']} valuePropName="checked"
                          style={{ marginBottom: 0 }} initialValue={false}>
                          <Switch size="small" checkedChildren="Req" unCheckedChildren="Opt"
                            checked={isDefault ? true : undefined} disabled={isDefault}
                            title={isDefault ? 'Locked — Default Field (always required)' : undefined} />
                        </Form.Item>
                      </>
                    );
                  })()}
                  <Form.Item {...restField} name={[rowName, 'showOnAdd']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={true}
                    tooltip="Show this field on Add. Turn off for edit-only fields.">
                    <Switch size="small" checkedChildren="Add" unCheckedChildren="No Add" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'edit']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={true}>
                    <Switch size="small" checkedChildren="Edit" unCheckedChildren="Read" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'addRow']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}
                    tooltip="Let users repeat this single field (add/remove multiple values). Ignored when this field's group is itself Add Row.">
                    <Switch size="small" checkedChildren="Repeat" unCheckedChildren="Single" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'clientScope']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}
                    tooltip="Use this field as the client-selector gate. It shows as the top 'Select Client' picker and is hidden from the form body (no duplicate client card).">
                    <Switch size="small" checkedChildren="Client Gate" unCheckedChildren="Client Gate" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'infoEnabled']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}
                    tooltip="Show an info icon next to this field label in add/edit forms.">
                    <Switch size="small" checkedChildren="Info" unCheckedChildren="Info" />
                  </Form.Item>
                </div>

                {/* Repeat (field-level add row) options — shown only when Repeat is on */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (!getFieldValue([...fAbs, 'addRow'])) return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Repeat Field Options</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'minRows']} label="Min Values"
                            style={{ marginBottom: 0 }}
                            tooltip="Minimum values the user must keep. Delete is disabled at this count.">
                            <Input size="small" type="number" min={0} placeholder="0" style={{ width: 90 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'initialRows']} label="Initial Values"
                            style={{ marginBottom: 0 }}
                            tooltip="How many empty inputs to show on a fresh Add (0 = show only the + button).">
                            <Input size="small" type="number" min={0} placeholder="1" style={{ width: 90 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'maxRows']} label="Max Values"
                            style={{ marginBottom: 0 }}
                            tooltip="Maximum values allowed. The + button hides at this count. Blank = unlimited.">
                            <Input size="small" type="number" min={0} placeholder="∞" style={{ width: 90 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'addLabel']} label="Add Button Label"
                            style={{ marginBottom: 0 }}
                            tooltip="Custom text for the add button. Defaults to 'Add <label>'.">
                            <Input size="small" placeholder="e.g. Add Skill" style={{ width: 170 }} />
                          </Form.Item>
                        </div>

                        {/* Sub-fields — turn this single repeat into a repeatable GROUP of
                            fields (e.g. Question + Answer). Leave empty to repeat a single
                            value. Each row then submits as an object, and the field as an
                            array of objects under its payload key. */}
                        <div className="fg-subfields-block" style={{ marginTop: 10 }}>
                          <span className="fg-options-label">
                            Sub-fields (repeat a group together)
                            <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                              leave empty to repeat a single value
                            </span>
                          </span>
                          <Form.List name={[rowName, 'subFields']}>
                            {(subItems, { add: addSub, remove: removeSub }) => (
                              <div>
                                {subItems.map(({ key: subKey, name: subName, ...subRest }) => (
                                  <div key={subKey} className="fg-subfield-row" style={{ marginBottom: 10 }}>
                                    <div className="fg-inline-row"
                                      style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                      <Form.Item {...subRest} name={[subName, 'field']} label="Key"
                                        rules={[
                                          { required: true, message: 'Key' },
                                          { pattern: /^[a-zA-Z0-9_]+$/, message: 'a-z 0-9 _' },
                                        ]}
                                        style={{ marginBottom: 0 }}>
                                        <Input size="small" placeholder="e.g. question"
                                          style={{ width: 140 }} className="fg-mono-input" />
                                      </Form.Item>
                                      <Form.Item {...subRest} name={[subName, 'label']} label="Label"
                                        rules={[{ required: true, message: 'Label' }]}
                                        style={{ marginBottom: 0 }}>
                                        <Input size="small" placeholder="e.g. Question" style={{ width: 150 }} />
                                      </Form.Item>
                                      <Form.Item {...subRest} name={[subName, 'type']} label="Type"
                                        initialValue="text" style={{ marginBottom: 0 }}>
                                        <Select size="small" options={FIELD_TYPES} style={{ width: 130 }} />
                                      </Form.Item>
                                      <Form.Item {...subRest} name={[subName, 'req']} label="Required"
                                        valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
                                        <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                                      </Form.Item>
                                      <Form.Item {...subRest} name={[subName, 'col']} label="Width"
                                        tooltip="Grid span: 12 = half row, 24 = full row. Blank = 12."
                                        style={{ marginBottom: 0 }}>
                                        <Input size="small" type="number" placeholder="12" style={{ width: 70 }} />
                                      </Form.Item>
                                      <Tooltip title="Remove sub-field">
                                        <button type="button" className="fg-field-remove-btn"
                                          onClick={() => removeSub(subName)} aria-label="Remove sub-field">
                                          <MinusCircleOutlined />
                                        </button>
                                      </Tooltip>
                                    </div>

                                    {/* A sub-field of type select/radio/checkbox needs the SAME
                                        dataSource config as a normal field (static options, master,
                                        group, or lookup) — without it the control renders with no
                                        options. Mirrors the top-level "Options / Values" block. */}
                                    <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                                      {() => {
                                        const subType = getFieldValue([...fAbs, 'subFields', subName, 'type']);
                                        if (!['select', 'radio', 'checkbox'].includes(subType)) return null;
                                        const subDs = getFieldValue([...fAbs, 'subFields', subName, 'dataSource']) ?? 'static';
                                        return (
                                          <div className="fg-options-block" style={{ marginTop: 6 }}>
                                            <span className="fg-options-label">Options / Values</span>
                                            <div className="fg-options-controls">
                                              <Form.Item name={[subName, 'dataSource']} label="Source"
                                                style={{ marginBottom: 0 }} initialValue="static">
                                                <Select size="small" style={{ width: 190 }} options={DROPDOWN_SOURCES} />
                                              </Form.Item>
                                              {subType === 'select' && (
                                                <Form.Item name={[subName, 'multiSelect']} label="Multi Select"
                                                  valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                                                  <Switch size="small" checkedChildren="Multi" unCheckedChildren="Single" />
                                                </Form.Item>
                                              )}
                                              {subDs === 'master' && (
                                                <Form.Item name={[subName, 'masterName']} label="Master Name"
                                                  style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter master name' }]}>
                                                  <Input size="small" placeholder="e.g. tracker" style={{ width: 140 }} />
                                                </Form.Item>
                                              )}
                                              {subDs === 'group' && (
                                                <Form.Item name={[subName, 'groupName']} label="Group Name"
                                                  style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter group name' }]}>
                                                  <Input size="small" placeholder="e.g. users" style={{ width: 140 }} />
                                                </Form.Item>
                                              )}
                                            </div>
                                            {subDs === 'static' && (
                                              <div className="fg-static-options">
                                                <span className="fg-static-options-hint">Label / Value pairs</span>
                                                <Form.List name={[subName, 'options']}>
                                                  {(optItems, { add: addOpt, remove: removeOpt }) => (
                                                    <>
                                                      {optItems.map(({ key: oKey, name: oName }) => (
                                                        <div key={oKey} className="fg-option-row">
                                                          <Form.Item name={[oName, 'label']} style={{ marginBottom: 0, flex: 1 }}>
                                                            <Input size="small" placeholder="Label" />
                                                          </Form.Item>
                                                          <Form.Item name={[oName, 'value']} style={{ marginBottom: 0, flex: 1 }}>
                                                            <Input size="small" placeholder="Value" className="fg-mono-input" />
                                                          </Form.Item>
                                                          <MinusCircleOutlined onClick={() => removeOpt(oName)} className="fg-remove-icon" />
                                                        </div>
                                                      ))}
                                                      <Button size="small" type="dashed" icon={<PlusOutlined />}
                                                        onClick={() => addOpt({ label: '', value: '' })}
                                                        className="fg-add-option-btn">
                                                        Add Option
                                                      </Button>
                                                    </>
                                                  )}
                                                </Form.List>
                                              </div>
                                            )}
                                            {subDs === 'group' && <p className="fg-source-hint">Use &quot;users&quot; to load all users from the MongoDB-backed user collection.</p>}
                                            {subDs === 'lookup' && (
                                              <div className="fg-lookup-block">
                                                <p className="fg-source-hint" style={{ marginBottom: 8 }}>Cross-collection lookup &mdash; shows label, stores FK value.</p>
                                                <div className="fg-field-meta-row">
                                                  <Form.Item name={[subName, 'lookupCollection']} label="Collection"
                                                    style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                                    <Input size="small" placeholder="e.g. vendors" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item name={[subName, 'displayField']} label="Display Field"
                                                    style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                                    <Input size="small" placeholder="e.g. vendorContact.firstName" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item name={[subName, 'displayField2']} label="Display Field 2"
                                                    style={{ marginBottom: 0, flex: 1 }}>
                                                    <Input size="small" placeholder="e.g. vendorContact.lastName" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item name={[subName, 'valueField']} label="Value Field (FK)"
                                                    style={{ marginBottom: 0, flex: 1 }}>
                                                    <Input size="small" placeholder="_id" className="fg-mono-input" />
                                                  </Form.Item>
                                                </div>
                                                <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                                                  <Form.Item name={[subName, 'lookupFilterField']} label="Filter Field"
                                                    style={{ marginBottom: 0, flex: 1 }}
                                                    tooltip="Optional DB field to filter lookup rows by. Example: _id or clientId.">
                                                    <Input size="small" placeholder="e.g. _id" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item name={[subName, 'lookupFilterValueFrom']} label="Filter Value From"
                                                    style={{ marginBottom: 0, flex: 1 }}
                                                    tooltip="Optional form/scope field that provides the filter value. Example: clientId.">
                                                    <Input size="small" placeholder="e.g. clientId" className="fg-mono-input" />
                                                  </Form.Item>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      }}
                                    </Form.Item>
                                  </div>
                                ))}
                                <Button type="dashed" size="small" icon={<PlusOutlined />}
                                  onClick={() => addSub({ type: 'text', req: false })}>
                                  Add Sub-field
                                </Button>
                              </div>
                            )}
                          </Form.List>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                  <Form.Item name={[rowName, 'placeholder']} label="Placeholder"
                    style={{ marginBottom: 0, flex: 1 }}>
                    <Input size="small" placeholder="Admin-controlled placeholder" />
                  </Form.Item>
                  <Form.Item name={[rowName, 'hidePlaceholder']} label="Hide Placeholder"
                    valuePropName="checked" style={{ marginBottom: 0 }}>
                    <Switch size="small" checkedChildren="Hidden" unCheckedChildren="Shown" />
                  </Form.Item>
                </div>

                <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                  <Form.Item name={[rowName, 'visiblePermission']} label="Visible permission key"
                    style={{ marginBottom: 0, flex: 1 }}
                    tooltip="Menu-permission key ('<module>.<key>', e.g. submission.viewRate) gating whether this field is rendered at all. Leave blank to show to everyone (subject to the Show toggle above). Add matching keys in Admin → Permissions.">
                    <Input size="small" placeholder="e.g. submission.viewRate" />
                  </Form.Item>
                  <Form.Item name={[rowName, 'editablePermission']} label="Editable permission key"
                    style={{ marginBottom: 0, flex: 1 }}
                    tooltip="Menu-permission key gating whether this field can be edited. Users without it see the field disabled with a tooltip. Leave blank to allow everyone (subject to the Edit toggle above).">
                    <Input size="small" placeholder="e.g. submission.editRate" />
                  </Form.Item>
                </div>

                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (!getFieldValue([...fAbs, 'clientScope'])) return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Client Gate Message</span>
                        <Form.Item name={[rowName, 'clientGateMessage']} style={{ marginBottom: 0 }}>
                          <Input.TextArea
                            rows={2}
                            placeholder="Message shown below 'Select Client' in the add form (leave blank for default)"
                          />
                        </Form.Item>
                        <Form.Item
                          name={[rowName, 'clientGateResetOnChange']}
                          valuePropName="checked"
                          style={{ margin: '10px 0 0' }}
                          tooltip="When the selected client changes or is cleared, remove every value already entered in the form before loading the new client configuration."
                        >
                          <Switch
                            size="small"
                            checkedChildren="Reset form on client change"
                            unCheckedChildren="Keep form values"
                          />
                        </Form.Item>
                      </div>
                    );
                  }}
                </Form.Item>

                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (!getFieldValue([...fAbs, 'infoEnabled'])) return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">
                          <InfoCircleOutlined /> Field Info
                        </span>
                        <Form.Item
                          name={[rowName, 'infoText']}
                          style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: 'Enter info message' }]}
                        >
                          <Input.TextArea
                            rows={2}
                            placeholder="Message shown when users hover the info icon"
                          />
                        </Form.Item>
                      </div>
                    );
                  }}
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[rowName, 'helpText']}
                  label="Extra Text"
                  tooltip="Small gray helper text rendered below this field in add/edit forms. Example: date format."
                >
                  <Input.TextArea rows={2} placeholder="Shown below the field" />
                </Form.Item>

                {/* Show Condition */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const watchField = getFieldValue([...fAbs, 'showIf', 'field']);
                    const op = getFieldValue([...fAbs, 'showIf', 'operator']) ?? 'eq';
                    const needsValue = SHOW_IF_VALUE_OPERATORS.includes(op);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Show Condition</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'showIf', 'field']} label="Show only when field"
                            style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="field key — blank = always show"
                              style={{ width: 210 }} className="fg-mono-input" />
                          </Form.Item>
                          {watchField && (
                            <>
                              <Form.Item name={[rowName, 'showIf', 'operator']} label="Operator"
                                style={{ marginBottom: 0 }} initialValue="eq">
                                <Select size="small" style={{ width: 130 }} options={SHOW_IF_OPERATORS}
                                  getPopupContainer={() => document.body} />
                              </Form.Item>
                              {needsValue && (
                                <Form.Item name={[rowName, 'showIf', 'value']} label="Value"
                                  style={{ marginBottom: 0 }}>
                                  <Input size="small" placeholder="e.g. yes" style={{ width: 100 }}
                                    className="fg-mono-input" />
                                </Form.Item>
                              )}
                            </>
                          )}
                        </div>
                        {watchField && (
                          <p className="fg-source-hint" style={{ marginTop: 4 }}>
                            Hidden until &ldquo;{watchField}&rdquo;
                            {needsValue
                              ? ` ${op === 'eq' ? 'equals' : op === 'neq' ? 'does not equal' : op === 'in' ? 'is one of' : 'is not one of'} the value above.`
                              : ` ${op === 'truthy' ? 'has any value.' : op === 'notEmpty' ? 'has at least one row.' : 'is empty.'}`}
                          </p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Conditional Label — changes display text only, never payload. */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const watchField = getFieldValue([...fAbs, 'labelWhen', 'field']);
                    const op = getFieldValue([...fAbs, 'labelWhen', 'operator']) ?? 'eq';
                    const needsValue = SHOW_IF_VALUE_OPERATORS.includes(op);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Conditional Label</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'labelWhen', 'field']} label="Watch field"
                            style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="blank = use normal label"
                              style={{ width: 190 }} className="fg-mono-input" />
                          </Form.Item>
                          {watchField && (
                            <>
                              <Form.Item name={[rowName, 'labelWhen', 'operator']} label="Operator"
                                style={{ marginBottom: 0 }} initialValue="eq">
                                <Select size="small" style={{ width: 130 }} options={SHOW_IF_OPERATORS}
                                  getPopupContainer={() => document.body} />
                              </Form.Item>
                              {needsValue && (
                                <Form.Item name={[rowName, 'labelWhen', 'value']} label="Value"
                                  style={{ marginBottom: 0 }}>
                                  <Input size="small" placeholder="comma-separated for one-of"
                                    style={{ width: 180 }} className="fg-mono-input" />
                                </Form.Item>
                              )}
                              <Form.Item name={[rowName, 'labelWhen', 'label']} label="Alternate label"
                                style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter alternate label' }]}>
                                <Input size="small" placeholder="e.g. Employer Rate" style={{ width: 170 }} />
                              </Form.Item>
                            </>
                          )}
                        </div>
                        {watchField && (
                          <p className="fg-source-hint" style={{ marginTop: 4 }}>
                            Only the displayed label changes. The field key and submitted payload stay unchanged.
                          </p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Verification — generic action/result configuration. */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const action = getFieldValue([...fAbs, 'verifyAction']);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Field Verification</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'verifyAction']} label="Action" style={{ marginBottom: 0 }}>
                            <Select size="small" allowClear placeholder="None" style={{ width: 130 }} options={[
                              { label: 'Email', value: 'email' },
                            ]} />
                          </Form.Item>
                          {action && (
                            <>
                              <Form.Item name={[rowName, 'verifyResultField']} label="Result field"
                                style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Result field is required' }]}>
                                <Input size="small" placeholder="e.g. isValid" style={{ width: 130 }} className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifyTimestampField']} label="Timestamp field" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="e.g. validatedAt" style={{ width: 150 }} className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifyIdleLabel']} label="Initial text" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="Verify Email" style={{ width: 130 }} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifyRetryLabel']} label="Retry text" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="Re Verify" style={{ width: 120 }} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifySuccessLabel']} label="Success text" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="Valid" style={{ width: 110 }} />
                              </Form.Item>
                            </>
                          )}
                        </div>
                        {action && (
                          <p className="fg-source-hint" style={{ marginTop: 4 }}>
                            Changing the verified value resets the result and disables any configured validation gate.
                          </p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Payload Mapping — config-driven transform engine */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    const dType = getFieldValue([...fAbs, 'dataType']) ?? 'auto';
                    const pMode = getFieldValue([...fAbs, 'payloadMode']) ?? 'auto';
                    const isChoice = ['select', 'radio', 'checkbox'].includes(fType);
                    const needsKeys = ['label', 'object', 'custom'].includes(pMode);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Payload Mapping</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'payloadKey']} label="Payload Key"
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="defaults to field key"
                              style={{ width: 180 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'prefillFrom']} label="Prefill From"
                            tooltip="Source path to inherit this field's value from when the record is created by prefilling another module (e.g. submission ← candidate). Dotted paths allowed, e.g. passport.passportNumber."
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="source key (cross-module prefill)"
                              style={{ width: 200 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'prefillFromModule']} label="Prefill From Module"
                            tooltip="Prefill this field from ANOTHER module's record linked in the URL instead of the default source — e.g. 'job' makes Quick Submit fill this field from the current job (with Prefill From naming the job's key, e.g. mspreqId). Leave blank for the normal prefill source."
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="e.g. job"
                              style={{ width: 160 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue, setFieldValue }) => {
                              const rows = getFieldValue(['fields', rowName, 'prefillWhen']) ?? [];
                              const lookupCollection = getFieldValue(['fields', rowName, 'lookupCollection']);
                              const displayField = getFieldValue(['fields', rowName, 'displayField']);
                              const valueField = getFieldValue(['fields', rowName, 'valueField']) || '_id';
                              const update = (i, patch) => {
                                const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
                                setFieldValue(['fields', rowName, 'prefillWhen'], next);
                              };
                              return (
                                <div style={{ width: '100%', margin: '4px 0 8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      Conditional Default — when a field matches, set this field to a chosen record
                                    </Text>
                                    <Button size="small" type="dashed" icon={<PlusOutlined />}
                                      onClick={() => setFieldValue(['fields', rowName, 'prefillWhen'], [...rows, { field: '', value: '', setValue: '' }])}>
                                      Add
                                    </Button>
                                  </div>
                                  {rows.map((row, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                                      <Input size="small" placeholder="when field (e.g. contractType)" className="fg-mono-input"
                                        style={{ width: 180 }} value={row.field}
                                        onChange={(e) => update(i, { field: e.target.value })} />
                                      <span style={{ color: '#94a3b8', fontSize: 12 }}>=</span>
                                      <Input size="small" placeholder="value (e.g. W2-Fulltime)" className="fg-mono-input"
                                        style={{ width: 150 }} value={row.value}
                                        onChange={(e) => update(i, { value: e.target.value })} />
                                      <span style={{ color: '#94a3b8', fontSize: 12 }}>→</span>
                                      <LookupRecordSelect
                                        collection={lookupCollection}
                                        displayField={displayField}
                                        valueField={valueField}
                                        value={row.setValue}
                                        labelValue={row.setValueLabel}
                                        onChange={(v, label) => update(i, { setValue: v, setValueLabel: label })}
                                      />
                                      <Button size="small" type="text" danger icon={<DeleteOutlined />}
                                        onClick={() => setFieldValue(['fields', rowName, 'prefillWhen'], rows.filter((_, idx) => idx !== i))} />
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          </Form.Item>
                          <Form.Item name={[rowName, 'dataType']} label="Data Type"
                            style={{ marginBottom: 8 }} initialValue="auto">
                            <Select size="small" style={{ width: 170 }} options={DATA_TYPES}
                              getPopupContainer={() => document.body} />
                          </Form.Item>
                          {dType === 'array' && (
                            <Form.Item name={[rowName, 'elementType']} label="Array Element Type"
                              style={{ marginBottom: 8 }}>
                              <Select size="small" style={{ width: 130 }} options={ELEMENT_TYPES}
                                getPopupContainer={() => document.body} />
                            </Form.Item>
                          )}
                          <Form.Item name={[rowName, 'payloadMode']} label="Payload Mode"
                            style={{ marginBottom: 8 }} initialValue="auto">
                            <Select size="small" style={{ width: 210 }} options={PAYLOAD_MODES}
                              getPopupContainer={() => document.body} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'defaultValue']} label="Default Value"
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="optional" style={{ width: 130 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'defaultOnEdit']} label="Default on Edit"
                            valuePropName="checked" style={{ marginBottom: 8 }}
                            tooltip="When the stored value is empty, show Default Value in edit form. Keep off for fields that should remain blank on edit.">
                            <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'emptyValues']} label="Empty Values"
                            style={{ marginBottom: 8 }}
                            tooltip="Comma separated stored values that should render as blank in edit form. Example: 0, candidate">
                            <Input size="small" placeholder="e.g. 0, candidate" style={{ width: 170 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'valuePaths']} label="Value Paths"
                            style={{ marginBottom: 8 }}
                            tooltip="Comma separated read paths for edit/detail prefill. Supports array filters like socialProfiles[type=linkedin].value.">
                            <Input size="small" placeholder="linkedIn, socialProfiles[type=linkedin].value"
                              style={{ width: 300 }} className="fg-mono-input" />
                          </Form.Item>
                        </div>

                        {(needsKeys || isChoice) && (
                          <div className="fg-field-meta-row">
                            <Form.Item name={[rowName, 'valueKey']} label="Value Key (id)"
                              style={{ marginBottom: 8, flex: 1 }}>
                              <Input size="small" placeholder="e.g. id" className="fg-mono-input" />
                            </Form.Item>
                            <Form.Item name={[rowName, 'displayKey']} label="Display Key (label)"
                              style={{ marginBottom: 8, flex: 1 }}>
                              <Input size="small" placeholder="e.g. name" className="fg-mono-input" />
                            </Form.Item>
                            {isChoice && (
                              <Form.Item name={[rowName, 'richValue']} label="Keep label"
                                valuePropName="checked" style={{ marginBottom: 8 }} initialValue={false}>
                                <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                              </Form.Item>
                            )}
                          </div>
                        )}

                        {pMode === 'custom' && (
                          <div style={{ marginBottom: 8 }}>
                            <span className="fg-static-options-hint">
                              Payload Template (JSON) — tokens: {'{{value}} {{label}} {{raw.field}}'}
                            </span>
                            <Form.Item name={[rowName, 'payloadTemplate']} style={{ marginBottom: 0 }}>
                              <Input.TextArea rows={2} className="fg-mono-input"
                                placeholder={'{ "countryId": "{{value}}", "countryName": "{{label}}" }'} />
                            </Form.Item>
                          </div>
                        )}

                        <details style={{ marginTop: 4 }}>
                          <summary className="fg-source-hint" style={{ cursor: 'pointer' }}>
                            Advanced: transform rule / omit empty
                          </summary>
                          <div style={{ marginTop: 6 }}>
                            <span className="fg-static-options-hint">
                              Transform Rule (JSON) — e.g. {'{ "map": { "true": "high", "false": "medium" } }'}
                            </span>
                            <Form.Item name={[rowName, 'transformRule']} style={{ marginBottom: 6 }}>
                              <Input.TextArea rows={2} className="fg-mono-input"
                                placeholder={'{ "map": { "true": "high" }, "default": "medium" }'} />
                            </Form.Item>
                            <Form.Item name={[rowName, 'omitEmpty']} label="Omit when empty"
                              valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                              <Switch size="small" checkedChildren="Omit" unCheckedChildren="Send" />
                            </Form.Item>
                          </div>
                        </details>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Input Validator — real-time character restriction */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    // Only text-like fields need input restriction
                    if (['select', 'radio', 'checkbox', 'date', 'time', 'file', 'location'].includes(fType)) return null;
                    const vType = getFieldValue([...fAbs, 'validator', 'type']);
                    const needsMaxLen  = vType && !['email', 'websiteUrl', 'jobDescription'].includes(vType);
                    const needsMaxChar = vType === 'jobDescription';
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Input Validator</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'validator', 'type']} label="Type"
                            style={{ marginBottom: 0 }}>
                            <Select
                              size="small"
                              allowClear
                              placeholder="None — no char restriction"
                              style={{ width: 260 }}
                              options={INPUT_VALIDATOR_TYPES}
                            />
                          </Form.Item>
                          {needsMaxLen && (
                            <Form.Item name={[rowName, 'validator', 'maxLength']} label="Max Length"
                              style={{ marginBottom: 0 }}>
                              <Input size="small" type="number" min={1} placeholder="e.g. 55" style={{ width: 90 }} />
                            </Form.Item>
                          )}
                          {needsMaxChar && (
                            <Form.Item name={[rowName, 'validator', 'maxChars']} label="Max Chars"
                              style={{ marginBottom: 0 }}>
                              <Input size="small" type="number" min={1} placeholder="1500" style={{ width: 90 }} />
                            </Form.Item>
                          )}
                          {vType && (
                            <Form.Item name={[rowName, 'validator', 'title']} label="Error Title"
                              style={{ marginBottom: 0 }}
                              tooltip="Used in error messages. Defaults to the field label when blank.">
                              <Input size="small" placeholder={getFieldValue([...fAbs, 'label']) || 'Field'} style={{ width: 180 }} />
                            </Form.Item>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Field Interactions — disabled-by / clear-on-check / autofill */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    const isChk = fType === 'checkbox';
                    const isLookup = fType === 'select' && getFieldValue([...fAbs, 'dataSource']) === 'lookup';
                    if (!isChk && !isLookup) {
                      return (
                        <div className="fg-options-block">
                          <span className="fg-options-label">Disable When Field Is Checked</span>
                          <Form.Item name={[rowName, 'disabledByField']} style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="sibling field key (e.g. currentlyPursuing)"
                              className="fg-mono-input" style={{ width: 260 }} />
                          </Form.Item>
                          <p className="fg-source-hint">This field becomes disabled (and read-only) when the specified sibling checkbox is checked.</p>
                        </div>
                      );
                    }
                    if (isChk) {
                      return (
                        <div className="fg-options-block">
                          <span className="fg-options-label">Clear Field When Checked</span>
                          <Form.Item name={[rowName, 'linkedClearField']} style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="sibling field key to clear (e.g. endDate)"
                              className="fg-mono-input" style={{ width: 260 }} />
                          </Form.Item>
                          <p className="fg-source-hint">When this checkbox is checked the specified sibling field is cleared and disabled.</p>
                        </div>
                      );
                    }
                    if (isLookup) {
                      return (
                        <div className="fg-options-block">
                          <span className="fg-options-label">Autofill Fields (JSON)</span>
                          <Form.Item name={[rowName, 'autofillFrom']} style={{ marginBottom: 0 }}>
                            <Input.TextArea rows={3} className="fg-mono-input"
                              placeholder={'[{"optionKey":"vendorContact.email","targetField":"vendorEmail"}]'} />
                          </Form.Item>
                          <p className="fg-source-hint">When a value is selected, copy raw lookup object fields into sibling fields. Each entry: optionKey (dotted path into the raw record) + targetField (sibling key).</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                </Form.Item>

                {/* Validations */}
                <div className="fg-validations-section">
                  <div className="fg-validations-header">
                    <SafetyCertificateOutlined className="fg-val-icon" />
                    <span className="fg-val-title">Validations</span>
                  </div>
                  <Form.List name={[rowName, 'validations']}>
                    {(valItems, { add: addVal, remove: removeVal }) => (
                      <>
                        {valItems.map(({ key: vKey, name: vName }) => (
                          <Form.Item shouldUpdate key={vKey} style={{ marginBottom: 6 }}>
                            {({ getFieldValue }) => {
                              const vType = getFieldValue([...fieldsAbs, rowName, 'validations', vName, 'type']);
                              const meta = validationMeta(vType);
                              const fieldType = getFieldValue([...fieldsAbs, rowName, 'type']);
                              const isDateRelation = vType === 'dateAfterField' || vType === 'dateBeforeField';
                              const siblingDateOptions = (getFieldValue(fieldsAbs) ?? [])
                                .map((candidate, index) => ({ candidate, index }))
                                .filter(({ candidate, index }) => index !== rowName && candidate?.type === 'date' && candidate?.field)
                                .map(({ candidate }) => ({
                                  value: candidate.field,
                                  label: `${candidate.label || candidate.field} (${candidate.field})`,
                                }));
                              const validationOptions = VALIDATION_TYPES
                                .filter((option) => fieldType === 'date'
                                  ? option.dateOnly || option.value === 'required'
                                  : !option.dateOnly)
                                .map((option) => ({ label: option.label, value: option.value }));
                              return (
                                <div className="fg-val-row">
                                  <Form.Item name={[vName, 'type']} style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Pick type' }]}>
                                    <Select size="small" placeholder={fieldType === 'date' ? 'Date condition' : 'Type'} style={{ width: 210 }}
                                      listHeight={400} getPopupContainer={() => document.body}
                                      options={validationOptions} />
                                  </Form.Item>
                                  {meta.hasValue && (
                                    <Form.Item name={[vName, 'value']} style={{ marginBottom: 0 }}>
                                      {isDateRelation ? (
                                        <Select
                                          size="small"
                                          showSearch
                                          allowClear
                                          placeholder="Related date field"
                                          style={{ width: 210 }}
                                          options={siblingDateOptions}
                                          optionFilterProp="label"
                                          getPopupContainer={() => document.body}
                                        />
                                      ) : (
                                        <Input size="small"
                                          type={meta.valueType === 'number' ? 'number' : 'text'}
                                          placeholder={meta.placeholder} style={{ width: 90 }} />
                                      )}
                                    </Form.Item>
                                  )}
                                  <Form.Item name={[vName, 'message']} style={{ marginBottom: 0, flex: 1 }}
                                    rules={meta.messageOptional ? [] : [{ required: true, message: 'Message required' }]}>
                                    <Input size="small"
                                      placeholder={meta.messageOptional ? 'Optional — uses default' : 'Error message shown to user'} />
                                  </Form.Item>
                                  <MinusCircleOutlined className="fg-remove-icon" style={{ marginTop: 4 }}
                                    onClick={() => removeVal(vName)} />
                                </div>
                              );
                            }}
                          </Form.Item>
                        ))}
                        <Button size="small" type="dashed" icon={<PlusOutlined />}
                          onClick={() => addVal({ type: '', value: '', message: '' })}
                          className="fg-add-val-btn">
                          Add Validation
                        </Button>
                      </>
                    )}
                  </Form.List>
                </div>

                {/* AI Actions — config-driven "Generate/Parse"-style buttons that call
                    an admin-configured AI/automation service and map its JSON response
                    onto form fields (scalar fields anywhere, or an addRow group's rows). */}
                <div className="fg-validations-section">
                  <div className="fg-validations-header">
                    <ThunderboltOutlined className="fg-val-icon" />
                    <span className="fg-val-title">AI Actions</span>
                  </div>
                  <Form.List name={[rowName, 'aiActions']}>
                    {(actionItems, { add: addAction, remove: removeAction }) => (
                      <>
                        {actionItems.map(({ key: aKey, name: aName }) => (
                          <div key={aKey} className="fg-ai-action-card">
                            <div className="fg-val-row">
                              <Form.Item name={[aName, 'key']} style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Key' }]}>
                                <Input size="small" placeholder="key (e.g. generateJD)" style={{ width: 150 }} />
                              </Form.Item>
                              <Form.Item name={[aName, 'label']} style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Label' }]}>
                                <Input size="small" placeholder="Button label" style={{ width: 170 }} />
                              </Form.Item>
                              <Form.Item name={[aName, 'trigger']} style={{ marginBottom: 0 }} initialValue="click">
                                <Select size="small" style={{ width: 100 }}
                                  options={[{ label: 'Click', value: 'click' }, { label: 'Upload', value: 'upload' }]} />
                              </Form.Item>
                              <Form.Item name={[aName, 'bodyType']} style={{ marginBottom: 0 }} initialValue="json">
                                <Select size="small" style={{ width: 110 }}
                                  options={[{ label: 'JSON', value: 'json' }, { label: 'Multipart', value: 'multipart' }]} />
                              </Form.Item>
                              <Form.Item name={[aName, 'position']} style={{ marginBottom: 0 }}>
                                <Select size="small" style={{ width: 150 }} placeholder="Button position" allowClear
                                  options={[
                                    { label: 'Top Left', value: 'top-left' },
                                    { label: 'Top Center', value: 'top-center' },
                                    { label: 'Top Right', value: 'top-right' },
                                    { label: 'Bottom Left', value: 'bottom-left' },
                                    { label: 'Bottom Center', value: 'bottom-center' },
                                    { label: 'Bottom Right', value: 'bottom-right' },
                                  ]} />
                              </Form.Item>
                              <MinusCircleOutlined className="fg-remove-icon" style={{ marginTop: 4 }}
                                onClick={() => removeAction(aName)} />
                            </div>
                            <div className="fg-val-row">
                              <Form.Item name={[aName, 'serviceKey']} style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Service key' }]}>
                                <Input size="small" placeholder="Service key (e.g. pyOps)" style={{ width: 170 }} />
                              </Form.Item>
                              <Form.Item name={[aName, 'endpointPath']} style={{ marginBottom: 0, flex: 1 }}
                                rules={[{ required: true, message: 'Endpoint path' }]}>
                                <Input size="small" placeholder="/v1/promtJD" className="fg-mono-input" />
                              </Form.Item>
                            </div>

                            <div className="fg-ai-subsection">
                              <span className="fg-options-label">
                                Inputs (form field → request param) — mark an input "Required", or
                                give it a Min Words count, to make the button stay disabled until
                                that field has a value (or enough words). Inputs sharing the same
                                "group" gate as one unit: either one satisfies it (e.g. a JD file
                                OR JD text).
                              </span>
                              <Form.List name={[aName, 'inputs']}>
                                {(inputItems, { add: addInput, remove: removeInput }) => (
                                  <>
                                    {inputItems.map(({ key: iKey, name: iName }) => (
                                      <div key={iKey} className="fg-val-row">
                                        <Form.Item name={[iName, 'param']} style={{ marginBottom: 0 }}>
                                          <Input size="small" placeholder="param" style={{ width: 100 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'sourceField']} style={{ marginBottom: 0 }}>
                                          <Input size="small" placeholder="source field" className="fg-mono-input" style={{ width: 150 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'kind']} style={{ marginBottom: 0 }} initialValue="text">
                                          <Select size="small" style={{ width: 90 }}
                                            options={[{ label: 'Text', value: 'text' }, { label: 'File', value: 'file' }]} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'transform']} style={{ marginBottom: 0 }}>
                                          <Input size="small" placeholder="transform" style={{ width: 120 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'group']} style={{ marginBottom: 0 }}>
                                          <Input size="small" placeholder="group (either/or)" style={{ width: 130 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'minWords']} style={{ marginBottom: 0 }}>
                                          <InputNumber size="small" min={0} placeholder="min words" style={{ width: 100 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'required']} valuePropName="checked" style={{ marginBottom: 0 }}>
                                          <Checkbox>Required</Checkbox>
                                        </Form.Item>
                                        <MinusCircleOutlined className="fg-remove-icon" onClick={() => removeInput(iName)} />
                                      </div>
                                    ))}
                                    <Button size="small" type="dashed" icon={<PlusOutlined />}
                                      onClick={() => addInput({ kind: 'text' })}>
                                      Add Input
                                    </Button>
                                  </>
                                )}
                              </Form.List>
                            </div>

                            <div className="fg-ai-subsection">
                              <span className="fg-options-label">
                                Response Data Path (optional — where the record lives in the
                                service's response envelope, e.g. "data"; the response is then
                                resolved onto this module's fields the exact same way Edit
                                prefill resolves getFormGroups — by field name, ValuePaths, and
                                each addRow group's payloadKey)
                              </span>
                              <Form.Item name={[aName, 'responseDataPath']} style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="e.g. data" className="fg-mono-input" style={{ width: 200 }} />
                              </Form.Item>
                            </div>
                          </div>
                        ))}
                        <Button size="small" type="dashed" icon={<PlusOutlined />}
                          onClick={() => addAction({ trigger: 'click', bodyType: 'json', inputs: [] })}
                          className="fg-add-val-btn">
                          Add AI Action
                        </Button>
                      </>
                    )}
                  </Form.List>
                </div>

              </div>
              <Tooltip title="Remove field">
                <button type="button" className="fg-field-remove-btn" onClick={() => remove(rowName)}
                  aria-label="Remove field">
                  <MinusCircleOutlined />
                </button>
              </Tooltip>
                </>
              )}
            </div>
          </div>
        );
      })}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => {
          add({ type: 'text', show: true, req: false, edit: true, dataSource: 'static', masterName: '', options: [] });
          onToggleField?.(fieldItems.length, true);
        }}
        block
        className="fg-add-field-btn"
      >
        Add Field
      </Button>
    </>
  );
}

// ── Group Drawer ──────────────────────────────────────────────────────────────
// onSaveGroup(payload, editingGroup) — when provided, replaces the default API calls.
function GroupDrawer({ open, editingGroup, defaultModule, moduleOptions, allGroupsByModule, onClose, onSaved, onSaveGroup }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [formModule, setFormModule] = useState(defaultModule);
  const [moduleFields, setModuleFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  // Default Fields (Form Groups → Default Fields) for the current module — their
  // Show/Required toggles are locked ON here so no one can turn a default field
  // off from the normal field editor.
  const [defaultFieldSet, setDefaultFieldSet] = useState(() => new Set());
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [expandedFieldKeys, setExpandedFieldKeys] = useState(() => new Set());
  const [editorTab, setEditorTab] = useState('settings');
  const [fieldsVisited, setFieldsVisited] = useState(false);
  const loadedRef = useRef({});
  const dragIndexRef = useRef(null);

  useEffect(() => {
    if (!open || !formModule) return;
    let cancelled = false;
    getModuleDefaultFields(formModule)
      .then((keys) => { if (!cancelled) setDefaultFieldSet(new Set(Array.isArray(keys) ? keys : [])); })
      .catch(() => { if (!cancelled) setDefaultFieldSet(new Set()); });
    return () => { cancelled = true; };
  }, [open, formModule]);

  async function ensureModuleFields(mod) {
    if (!mod || loadedRef.current[mod]) return;
    loadedRef.current[mod] = 'loading';
    setFieldsLoading(true);
    try {
      const fields = await getModuleFields(mod);
      setModuleFields((prev) => ({ ...prev, [mod]: fields }));
      loadedRef.current[mod] = 'loaded';
    } catch {
      delete loadedRef.current[mod]; // allow retry; free-text remains available
    } finally {
      setFieldsLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    const mod = editingGroup?.module ?? defaultModule;
    setFormModule(mod);
    if (editingGroup) {
      form.setFieldsValue({
        module: editingGroup.module,
        name:   editingGroup.name,
        label:  editingGroup.label,
        addRow: editingGroup.addRow ?? false,
        hideGroup: editingGroup.hideGroup ?? false,
        moduleName: editingGroup.moduleName ?? '',
        // Linkage between this Target Collection group and the main record. Mode
        // is derived from which key is set: parentRefField → forward lookup
        // (this record holds the target's id); otherwise child→parent (target
        // holds this record's id via linkField / default parentId).
        linkMode: editingGroup.parentRefField ? 'forward' : 'reverse',
        linkField: editingGroup.linkField ?? '',
        linkTypeField: editingGroup.linkTypeField ?? '',
        parentRefField: editingGroup.parentRefField ?? '',
        writeRefs: editingGroup.writeRefs ?? [],
        dependsOn: editingGroup.dependsOn ?? [],
        optionalWrite: editingGroup.optionalWrite ?? false,
        appendHistory: editingGroup.appendHistory ?? false,
        historyField: [editingGroup.historyField, ...(editingGroup.historyFields ?? [])]
          .filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', '),
        historyTimestampField: editingGroup.historyTimestampField ?? '',
        minRows: editingGroup.minRows ?? '',
        maxRows: editingGroup.maxRows ?? '',
        initialRows: editingGroup.initialRows ?? '',
        requireOne: editingGroup.requireOne ?? false,
        requireValidatedField: editingGroup.requireValidatedField ?? '',
        linkGroup: editingGroup.linkGroup ?? '',
        showIfField: editingGroup.showIf?.field ?? '',
        showIfOperator: editingGroup.showIf?.operator ?? 'notEmpty',
        showIfValue: editingGroup.showIf?.value ?? '',
        infoEnabled: editingGroup.infoEnabled ?? false,
        infoText: editingGroup.infoText ?? '',
        toggleEnabled: editingGroup.toggleEnabled ?? false,
        toggleLabel: editingGroup.toggleLabel ?? '',
        toggleField: editingGroup.toggleField ?? '',
        afterSubmitMode: editingGroup.afterSubmit?.mode ?? undefined,
        afterSubmitTarget: editingGroup.afterSubmit?.target ?? '',
        formActions: (editingGroup.formActions ?? []).map((a) => ({
          label: a.label ?? '',
          tone: a.tone === 'primary' ? 'primary' : 'default',
          afterSubmitMode: a.afterSubmit?.mode ?? 'stay',
          afterSubmitTarget: a.afterSubmit?.target ?? '',
          enabledWhenField: a.enabledWhen?.field ?? '',
          enabledWhenOperator: a.enabledWhen?.operator ?? 'notEmpty',
          enabledWhenValue: a.enabledWhen?.value ?? '',
        })),
        submitMsgAddSuccess: editingGroup.submitMessages?.addSuccess ?? '',
        submitMsgEditSuccess: editingGroup.submitMessages?.editSuccess ?? '',
        submitMsgAddError: editingGroup.submitMessages?.addError ?? '',
        submitMsgEditError: editingGroup.submitMessages?.editError ?? '',
        submitMsgSuppress: editingGroup.submitMessages?.suppress ?? false,
        order:  editingGroup.order ?? 0,
        // Load fields in their saved display order so the drag-and-drop editor
        // and the rendered form always agree (both key off `order`).
        fields: [...(editingGroup.fields ?? [])]
          .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
          .map(initFieldValues),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ module: mod, addRow: false, hideGroup: false, moduleName: '', linkMode: 'reverse', linkField: '', linkTypeField: '', parentRefField: '', appendHistory: false, historyField: '', historyTimestampField: '', minRows: '', requireOne: false, showIfField: '', showIfOperator: 'notEmpty', showIfValue: '', formActions: [], infoEnabled: false, toggleEnabled: false, afterSubmitMode: undefined, afterSubmitTarget: '', order: 0, fields: [] });
    }
    ensureModuleFields(mod);
  }, [open, editingGroup, defaultModule, form]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleModuleChange(mod) { setFormModule(mod); ensureModuleFields(mod); }

  function closeEditor() {
    setExpandedFieldKeys(new Set());
    setEditorTab('settings');
    setFieldsVisited(false);
    onClose();
  }

  function selectEditorTab(tab) {
    setEditorTab(tab);
    if (tab === 'fields') setFieldsVisited(true);
  }

  function toggleField(key, forceOpen = false) {
    setExpandedFieldKeys((current) => {
      const next = new Set(current);
      if (forceOpen) next.add(key);
      else if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function usedFieldKeys(mod) {
    const groups = allGroupsByModule[mod] ?? [];
    const used = new Set();
    groups.forEach((g) => {
      if (editingGroup && g.id === editingGroup.id) return;
      (g.fields ?? []).forEach((fld) => { if (fld.field) used.add(fld.field); });
    });
    return used;
  }

  function handleFieldKeySelect(fieldPath, rowName) {
    const apiFields = moduleFields[formModule] ?? [];
    const match = apiFields.find((f) => f.value === fieldPath);
    if (!match) return;
    const current = form.getFieldValue(['fields', rowName]) ?? {};
    form.setFieldValue(['fields', rowName], { ...current, field: match.value, label: match.label, type: match.type ?? 'text' });
  }

  function fieldOptions(mod) {
    const taken = usedFieldKeys(mod);
    return (moduleFields[mod] ?? []).map((f) => ({
      value: f.value,
      disabled: taken.has(f.value),
      label: (
        <span style={{ opacity: taken.has(f.value) ? 0.4 : 1 }}>
          {f.label}
          <span className="fg-field-key-hint">{f.value}</span>
          {taken.has(f.value) && <span className="fg-field-taken-hint">(in another group)</span>}
        </span>
      ),
      searchLabel: `${f.label} ${f.value}`.toLowerCase(),
    }));
  }

  async function handleSave() {
    try {
      const validated = await form.validateFields();
      // Collapsed field editors are deliberately unmounted for speed. Read the
      // preserved form store so their untouched settings remain in the payload.
      const values = {
        ...form.getFieldsValue(true),
        ...validated,
        fields: form.getFieldValue('fields') ?? [],
      };
      setSaving(true);
      const payload = {
        module: values.module, name: values.name, label: values.label,
        addRow: values.addRow ?? false, order: Number(values.order ?? 0),
        fields: buildFieldPayload(values.fields ?? []),
      };
      if (values.hideGroup) payload.hideGroup = true;
      if (values.moduleName) {
        payload.moduleName = values.moduleName;
        // Linkage between this Target Collection group and the main record —
        // ids are stored as ObjectId either way. The two modes are mutually
        // exclusive; persist only the keys for the chosen mode so the backend's
        // mode inference (parentRefField set → forward) stays unambiguous.
        if (values.linkMode === 'forward') {
          const ref = String(values.parentRefField ?? '').trim();
          if (ref) payload.parentRefField = ref;
        } else {
          const lf = String(values.linkField ?? '').trim();
          const ltf = String(values.linkTypeField ?? '').trim();
          if (lf) payload.linkField = lf;
          if (ltf) payload.linkTypeField = ltf;
        }
        // Dependency config only means something for a Target Collection group.
        const refs = (values.writeRefs ?? []).filter((r) => r?.fromModule && r?.targetField);
        if (refs.length) payload.writeRefs = refs.map((r) => ({ fromModule: r.fromModule.trim(), targetField: r.targetField.trim() }));
        const deps = (values.dependsOn ?? []).map((d) => String(d).trim()).filter(Boolean);
        if (deps.length) payload.dependsOn = deps;
        if (values.optionalWrite) payload.optionalWrite = true;
      }
      // Append-history is independent of Target Collection — a plain group on
      // the module's OWN record whose value gets appended (with a timestamp)
      // instead of overwritten. historyField is required for it to do anything.
      // Comma-separated input → one history container per prefix (a group whose
      // fields span several prefixes keeps an independent timestamped array for
      // each, e.g. "candidateBudget, proposedRate, referralRate").
      const historyContainers = String(values.historyField ?? '')
        .split(',').map((v) => v.trim()).filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i);
      if (values.appendHistory && historyContainers.length) {
        payload.appendHistory = true;
        payload.historyField = historyContainers[0];
        if (historyContainers.length > 1) payload.historyFields = historyContainers.slice(1);
        if (String(values.historyTimestampField ?? '').trim()) {
          payload.historyTimestampField = values.historyTimestampField.trim();
        }
      }
      if (values.addRow && values.minRows !== undefined && values.minRows !== '') {
        payload.minRows = Number(values.minRows);
      }
      if (values.addRow && values.initialRows !== undefined && values.initialRows !== '') {
        payload.initialRows = Number(values.initialRows);
      }
      if (values.addRow && values.maxRows !== undefined && values.maxRows !== '') {
        payload.maxRows = Number(values.maxRows);
      }
      if (values.addRow && String(values.requireValidatedField ?? '').trim()) {
        payload.requireValidatedField = values.requireValidatedField.trim();
      }
      if (values.addRow && values.requireOne) payload.requireOne = true;
      if (values.addRow && values.linkGroup) payload.linkGroup = values.linkGroup.trim();
      // Group-level showIf — gate the whole group on another field's value.
      if (String(values.showIfField ?? '').trim()) {
        const operator = values.showIfOperator || 'notEmpty';
        payload.showIf = { field: values.showIfField.trim(), operator };
        if (['eq', 'neq', 'in', 'notIn'].includes(operator) && String(values.showIfValue ?? '').trim()) {
          payload.showIf.value = values.showIfValue.trim();
        }
      }
      if (values.infoEnabled && values.infoText) {
        payload.infoEnabled = true;
        payload.infoText = values.infoText;
      }
      if (values.toggleEnabled && values.toggleField) {
        payload.toggleEnabled = true;
        payload.toggleLabel = values.toggleLabel || 'Toggle';
        payload.toggleField = values.toggleField;
      }
      if (values.afterSubmitMode) {
        payload.afterSubmit = { mode: values.afterSubmitMode };
        if (values.afterSubmitMode === 'route' || values.afterSubmitMode === 'detail') {
          payload.afterSubmit.target = (values.afterSubmitTarget || '').trim();
        }
      }
      // Additional submit buttons (e.g. "Create and Source Candidate"): each has
      // its own label, tone, after-submit landing and optional enable-when gate.
      const actions = (values.formActions ?? []).filter((a) => String(a?.label ?? '').trim());
      if (actions.length) {
        payload.formActions = actions.map((a) => {
          const out = { label: a.label.trim(), tone: a.tone === 'primary' ? 'primary' : 'default' };
          const mode = a.afterSubmitMode || 'stay';
          out.afterSubmit = { mode };
          if (mode === 'route' || mode === 'detail') out.afterSubmit.target = String(a.afterSubmitTarget ?? '').trim();
          if (String(a.enabledWhenField ?? '').trim()) {
            const operator = a.enabledWhenOperator || 'notEmpty';
            out.enabledWhen = { field: a.enabledWhenField.trim(), operator };
            if (['eq', 'neq', 'in', 'notIn'].includes(operator) && String(a.enabledWhenValue ?? '').trim()) {
              out.enabledWhen.value = a.enabledWhenValue.trim();
            }
          }
          return out;
        });
      }
      // Submit toast overrides. Only persisted when something differs from the
      // built-in defaults, so untouched groups stay clean (nil in Mongo) and the
      // frontend keeps using its defaults everywhere else.
      const submitMessages = {};
      if ((values.submitMsgAddSuccess || '').trim()) submitMessages.addSuccess = values.submitMsgAddSuccess.trim();
      if ((values.submitMsgEditSuccess || '').trim()) submitMessages.editSuccess = values.submitMsgEditSuccess.trim();
      if ((values.submitMsgAddError || '').trim()) submitMessages.addError = values.submitMsgAddError.trim();
      if ((values.submitMsgEditError || '').trim()) submitMessages.editError = values.submitMsgEditError.trim();
      if (values.submitMsgSuppress) submitMessages.suppress = true;
      if (Object.keys(submitMessages).length) payload.submitMessages = submitMessages;
      // Preserve group config keys this editor doesn't manage (payloadKey,
      // sharedRowLabels, linkField/linkTypeField, showIf, …) — before this,
      // editing ANY group here silently dropped them (e.g. workExperience's
      // payloadKey), breaking the rendered form. Managed keys are excluded so
      // clearing them in the UI still clears them.
      if (editingGroup) {
        for (const [k, v] of Object.entries(editingGroup)) {
          if (v === undefined || v === null || v === '') continue;
          if (k in payload || GROUP_EDITOR_MANAGED_KEYS.has(k)) continue;
          payload[k] = v;
        }
      }
      if (onSaveGroup) {
        await onSaveGroup(payload, editingGroup);
        message.success(editingGroup ? 'Group updated' : 'Group created');
      } else if (editingGroup) {
        await updateAdminFormGroup(editingGroup.id, payload);
        message.success('Group updated');
      } else {
        await createAdminFormGroup(payload);
        message.success('Group created');
      }
      setExpandedFieldKeys(new Set());
      setEditorTab('settings');
      setFieldsVisited(false);
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not save group');
    } finally { setSaving(false); }
  }

  const options = useMemo(
    () => fieldOptions(formModule || defaultModule),
    // Group/field inputs are intentionally the cache boundary for option building.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allGroupsByModule, defaultModule, editingGroup, formModule, moduleFields],
  );

  return (
    <Modal
      open={open}
      onCancel={closeEditor}
      width={1080}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal fg-group-editor-modal"
      title={
        <span className="fg-drawer-title">
          {editingGroup ? <EditOutlined /> : <PlusOutlined />}
          {editingGroup ? ' Edit Form Group' : ' New Form Group'}
        </span>
      }
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={closeEditor}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}
            icon={editingGroup ? <EditOutlined /> : <PlusOutlined />}>
            {editingGroup ? 'Update Group' : 'Create Group'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" className="fg-form fg-editor-form fg-group-editor-form">
        <div className="fg-group-editor-tabs" role="tablist" aria-label="Form group editor sections">
          <button
            type="button"
            role="tab"
            aria-selected={editorTab === 'settings'}
            className={editorTab === 'settings' ? 'is-active' : ''}
            onClick={() => selectEditorTab('settings')}
          >
            <span className="fg-editor-tab-number">1</span>
            <span><strong>Group settings</strong><small>Name, behaviour and display options</small></span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={editorTab === 'fields'}
            className={editorTab === 'fields' ? 'is-active' : ''}
            onClick={() => selectEditorTab('fields')}
          >
            <span className="fg-editor-tab-number">2</span>
            <span><strong>Fields</strong><small>{editingGroup?.fields?.length ?? 0} configured fields</small></span>
          </button>
        </div>

        <div className={`fg-group-editor-panel${editorTab === 'settings' ? ' is-active' : ''}`}>
        <div className="fg-form-section fg-group-settings-card">
          <div className="fg-form-section-label">Group Info</div>
          <Form.Item name="module" label="Module" rules={[{ required: true, message: 'Select a module' }]}>
            <Select options={moduleOptions} placeholder="Select module" onChange={handleModuleChange} />
          </Form.Item>
          <Form.Item name="label" label="Group Label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input placeholder="e.g. Work Experience" />
          </Form.Item>
          <Form.Item name="name" label="Group Name (key)"
            rules={[
              { required: true, message: 'Name is required' },
              { pattern: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers, underscores only' },
            ]}>
            <Input placeholder="e.g. work_experience" className="fg-mono-input" />
          </Form.Item>
          <Form.Item name="moduleName" label="Target Collection (optional)"
            tooltip="Route THIS group's fields to a different collection instead of the default module record. Groups sharing the same target merge into one linked record. Leave blank for default behaviour.">
            <AutoComplete
              options={moduleOptions.map((m) => ({ value: m.value, label: m.label }))}
              placeholder="Default (this module's own record)"
              allowClear
              filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
              className="fg-mono-input"
            />
          </Form.Item>
          {/* Linkage between a Target Collection group and the main record.
              Shown only when a Target Collection is chosen. Ids are stored as
              ObjectId in either direction; the two modes are mutually exclusive. */}
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('moduleName') ? (
              <div className="fg-options-block" style={{ marginBottom: 12 }}>
                <span className="fg-options-label">
                  Linkage
                  <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                    how this collection links to the main record (ids stored as ObjectId)
                  </span>
                </span>
                <Form.Item name="linkMode" style={{ marginBottom: 8 }}>
                  <Radio.Group optionType="button" buttonStyle="solid" size="small">
                    <Radio.Button value="reverse">Target stores this record’s id (child→parent)</Radio.Button>
                    <Radio.Button value="forward">This record stores the target’s id (lookup)</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                  {({ getFieldValue: gv }) => gv('linkMode') === 'forward' ? (
                    <Form.Item name="parentRefField" label="Reference field on THIS record"
                      tooltip="A field on the main record that holds the target document's _id (as ObjectId), e.g. employerRef. On edit it selects which target document to load and update."
                      style={{ marginBottom: 0 }}>
                      <Input placeholder="e.g. employerRef" className="fg-mono-input" style={{ width: 260 }} />
                    </Form.Item>
                  ) : (
                    <div className="fg-inline-row" style={{ alignItems: 'flex-end' }}>
                      <Form.Item name="linkField" label="Link field on target (optional)"
                        tooltip="Field on the TARGET document that stores this record's _id (as ObjectId), e.g. jobRef. Leave blank to use the default parentId / parentModule linkage."
                        style={{ marginBottom: 0 }}>
                        <Input placeholder="e.g. jobRef" className="fg-mono-input" style={{ width: 220 }} />
                      </Form.Item>
                      <Form.Item name="linkTypeField" label="Parent-type field (optional)"
                        tooltip="Field on the target document recording WHICH module the parent is (e.g. parentType), so one collection can serve several parent modules."
                        style={{ marginBottom: 0 }}>
                        <Input placeholder="e.g. parentType" className="fg-mono-input" style={{ width: 220 }} />
                      </Form.Item>
                    </div>
                  )}
                </Form.Item>
              </div>
            ) : null}
          </Form.Item>
          {/* Dependency config for a Target Collection group: which other writes
              of the same submit this one depends on, and where their created
              document ids are injected. Backend validates the graph (missing /
              circular refs reject the submit before anything is written) and
              rolls back ALL writes when a required one fails. */}
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('moduleName') ? (
              <div className="fg-options-block" style={{ marginBottom: 12 }}>
                <span className="fg-options-label">
                  Linked-write dependencies
                  <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                    inject ids created by other writes of this same form submit
                  </span>
                </span>
                <Form.List name="writeRefs">
                  {(refItems, { add: addRef, remove: removeRef }) => (
                    <div>
                      {refItems.map(({ key: rKey, name: rName, ...rRest }) => (
                        <div key={rKey} className="fg-inline-row" style={{ alignItems: 'flex-end', marginBottom: 6 }}>
                          <Form.Item {...rRest} name={[rName, 'fromModule']} label="Id from (module)"
                            rules={[{ required: true, message: 'Module' }]} style={{ marginBottom: 0 }}>
                            <AutoComplete
                              options={moduleOptions.map((m) => ({ value: m.value }))}
                              placeholder="e.g. users (or this module)"
                              style={{ width: 220 }} className="fg-mono-input"
                              filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                            />
                          </Form.Item>
                          <Form.Item {...rRest} name={[rName, 'targetField']} label="Store into field"
                            rules={[{ required: true, message: 'Field' }]} style={{ marginBottom: 0 }}>
                            <Input placeholder="e.g. userId or links.userId" style={{ width: 220 }} className="fg-mono-input" />
                          </Form.Item>
                          <Tooltip title="Remove dependency">
                            <button type="button" className="fg-field-remove-btn" onClick={() => removeRef(rName)} aria-label="Remove dependency">
                              <MinusCircleOutlined />
                            </button>
                          </Tooltip>
                        </div>
                      ))}
                      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => addRef({ fromModule: '', targetField: '' })}>
                        Add Dependency
                      </Button>
                    </div>
                  )}
                </Form.List>
                <div className="fg-inline-row" style={{ marginTop: 10 }}>
                  <Form.Item name="dependsOn" label="Runs after (ordering only)"
                    tooltip="Other Target Collections this write must wait for, without injecting an id."
                    style={{ marginBottom: 0, flex: 1 }}>
                    <Select mode="tags" placeholder="e.g. users" options={moduleOptions.map((m) => ({ value: m.value, label: m.value }))} />
                  </Form.Item>
                  <Form.Item name="optionalWrite" label="Optional write" valuePropName="checked"
                    tooltip="On: a failure of this write is logged but the submit still succeeds (best-effort). Off (default): a failure rolls back EVERY write of this submit."
                    style={{ marginBottom: 0 }}>
                    <Switch checkedChildren="Best-effort" unCheckedChildren="Required" />
                  </Form.Item>
                </div>
              </div>
            ) : null}
          </Form.Item>

          {/* Append-history: independent of Target Collection — this group's
              own resolved value (on the module's own record) gets APPENDED
              with a timestamp instead of overwritten. Detail view / edit
              prefill still show only the LATEST entry, unaffected. */}
          <Form.Item shouldUpdate style={{ marginBottom: 12 }} className="fg-group-conditional">
            {({ getFieldValue }) => {
              const appendHistoryOn = getFieldValue('appendHistory');
              return (
                <div className="fg-options-block">
                  <span className="fg-options-label">
                    Append as history
                    <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                      keep every past save instead of overwriting (e.g. rate negotiation history)
                    </span>
                  </span>
                  <div className="fg-options-controls">
                    <Form.Item name="appendHistory" valuePropName="checked" style={{ marginBottom: 0 }}>
                      <Switch checkedChildren="Append" unCheckedChildren="Off" />
                    </Form.Item>
                    {appendHistoryOn && (
                      <>
                        <Form.Item name="historyField" label="Array field"
                          rules={[{ required: true, message: 'Required' }]}
                          tooltip="The record key(s) holding the append-only array(s) — usually the prefix(es) this group's own fields already write through. Comma-separate to keep an independent history per prefix, e.g. candidateBudget, proposedRate, referralRate."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="e.g. proposedRate, candidateBudget" style={{ width: 240 }} className="fg-mono-input" />
                        </Form.Item>
                        <Form.Item name="historyTimestampField" label="Timestamp key"
                          tooltip="Key stamped onto each saved entry. Blank defaults to 'timestamp'."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="timestamp" style={{ width: 140 }} className="fg-mono-input" />
                        </Form.Item>
                      </>
                    )}
                  </div>
                  {appendHistoryOn && (
                    <p className="fg-source-hint" style={{ marginTop: 6 }}>
                      Every save adds a new entry to the end of the array — the detail view and edit form
                      always show the latest one, exactly like a normal field.
                    </p>
                  )}
                </div>
              );
            }}
          </Form.Item>

          <div className="fg-inline-row">
            <Form.Item name="order" label="Display Order">
              <Input type="number" min={0} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="addRow" label="Allow Add Row" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="hideGroup" label="Hide Group" valuePropName="checked"
              tooltip="Hide this group entirely from Add/Edit forms — not rendered, not validated, not submitted.">
              <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
            </Form.Item>
            <Form.Item label="Show group when"
              tooltip="Gate the whole group on another field's live value — the group only appears once the condition matches (e.g. the Employer group opens after Contract Type is chosen). Leave the field blank to always show.">
              <Space.Compact block>
                <Form.Item name="showIfField" noStyle>
                  <Input placeholder="Field key e.g. contractType" style={{ width: '45%' }} />
                </Form.Item>
                <Form.Item name="showIfOperator" noStyle>
                  <Select style={{ width: '30%' }} options={[
                    { value: 'notEmpty', label: 'is set' },
                    { value: 'isEmpty', label: 'is empty' },
                    { value: 'eq', label: 'equals' },
                    { value: 'neq', label: 'not equals' },
                    { value: 'in', label: 'in list' },
                    { value: 'notIn', label: 'not in list' },
                  ]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.showIfOperator !== cur.showIfOperator}>
                  {({ getFieldValue }) => (['eq', 'neq', 'in', 'notIn'].includes(getFieldValue('showIfOperator'))
                    ? <Form.Item name="showIfValue" noStyle><Input placeholder="Value(s), comma for list" style={{ width: '25%' }} /></Form.Item>
                    : null)}
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item name="minRows" label="Min Rows"
              tooltip="Minimum rows users must keep. Delete is disabled at this count.">
              <Input type="number" min={0} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="maxRows" label="Max Rows"
              tooltip="Maximum rows users can add. The Add button hides once this count is reached. Blank/0 = unlimited.">
              <Input type="number" min={0} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="initialRows" label="Initial Rows"
              tooltip="How many empty rows to show on load (0 = show only the + button).">
              <Input type="number" min={0} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="requireOne" label="Row Required" valuePropName="checked"
              tooltip="When users add a row, at least one field in that row must be filled.">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="requireValidatedField" label="Require Validated Field"
              tooltip="A boolean field key on every row (e.g. isValid, set by a field's Verify action). The form's Submit button stays disabled until every row has it set to true. Blank = no gate.">
              <Input placeholder="e.g. isValid" style={{ width: 130 }} className="fg-mono-input" />
            </Form.Item>
            <Form.Item name="linkGroup" label="Link Group"
              tooltip="Groups sharing the same value here stay row-synced — adding or removing a row in one adds/removes the matching row in every linked group, while each keeps its own card and its own submitted data. Leave blank for an independent group. Only applies when Allow Add Row is on.">
              <Input placeholder="e.g. workHistory" style={{ width: 160 }} className="fg-mono-input" />
            </Form.Item>
            <Form.Item name="infoEnabled" label="Group Info" valuePropName="checked" initialValue={false}>
              <Switch checkedChildren="Info" unCheckedChildren="Info" />
            </Form.Item>
            <Form.Item name="toggleEnabled" label="Group Switch" valuePropName="checked" initialValue={false}>
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
          </div>
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('infoEnabled') ? (
              <Form.Item
                name="infoText"
                label="Group Info Message"
                rules={[{ required: true, message: 'Enter group info message' }]}
              >
                <Input.TextArea rows={2} placeholder="Message shown when users hover the group info icon" />
              </Form.Item>
            ) : null}
          </Form.Item>
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('toggleEnabled') ? (
              <div className="fg-inline-row">
                <Form.Item
                  name="toggleLabel"
                  label="Switch Label"
                  rules={[{ required: true, message: 'Enter switch label' }]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="e.g. Fresher" />
                </Form.Item>
                <Form.Item
                  name="toggleField"
                  label="Payload Field"
                  rules={[
                    { required: true, message: 'Enter payload field' },
                    { pattern: /^[A-Za-z_][\w.]*$/, message: 'Use a valid key, dot path allowed' },
                  ]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="e.g. isFresher" className="fg-mono-input" />
                </Form.Item>
              </div>
            ) : null}
          </Form.Item>
          <div className="fg-inline-row">
            <Form.Item name="afterSubmitMode" label="After Submit"
              tooltip="What happens once this module's Add/Edit form submits successfully. Leave blank to keep the current default navigation.">
              <Select
                allowClear
                placeholder="Default (current behavior)"
                style={{ width: 220 }}
                options={[
                  { value: 'stay', label: 'Stay on Current Page' },
                  { value: 'back', label: 'Go Back' },
                  { value: 'route', label: 'Navigate to a Route' },
                  { value: 'detail', label: 'Navigate to Record Detail' },
                ]}
              />
            </Form.Item>
          </div>
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => {
              const mode = getFieldValue('afterSubmitMode');
              if (mode !== 'route' && mode !== 'detail') return null;
              return (
                <Form.Item
                  name="afterSubmitTarget"
                  label="Navigation Target"
                  tooltip="An in-app path. Use :id for the created/updated record's id, or :fieldName for any submitted field value."
                  rules={[
                    { required: true, message: 'Enter a navigation target' },
                    { pattern: /^\//, message: 'Must be an in-app path starting with /' },
                  ]}
                >
                  <Input placeholder="/trainers/:id" className="fg-mono-input" />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* Additional submit buttons (e.g. "Create and Source Candidate"). */}
          <Form.Item label="Additional submit buttons"
            tooltip="Extra buttons beside Create — each creates the record then navigates per its own After-Submit, optionally enabled only once a field is filled.">
            <Form.List name="formActions">
              {(rows, { add, remove }) => (
                <div className="fg-form-actions-list">
                  {rows.map(({ key, name }) => (
                    <div key={key} className="fg-form-action-row" style={{ border: '1px solid #eee', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                      <Space wrap align="start">
                        <Form.Item name={[name, 'label']} label="Button Label" rules={[{ required: true, message: 'Label' }]} style={{ marginBottom: 8 }}>
                          <Input placeholder="e.g. Create and Source Candidate" style={{ width: 240 }} />
                        </Form.Item>
                        <Form.Item name={[name, 'tone']} label="Type" style={{ marginBottom: 8 }}>
                          <Select style={{ width: 130 }} options={[
                            { value: 'primary', label: 'Primary' },
                            { value: 'default', label: 'Secondary' },
                          ]} />
                        </Form.Item>
                        <Form.Item name={[name, 'afterSubmitMode']} label="After Submit" style={{ marginBottom: 8 }}>
                          <Select style={{ width: 180 }} options={[
                            { value: 'stay', label: 'Stay on page' },
                            { value: 'back', label: 'Go back' },
                            { value: 'route', label: 'Navigate to a route' },
                            { value: 'detail', label: 'Record detail' },
                          ]} />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => (['route', 'detail'].includes(getFieldValue(['formActions', name, 'afterSubmitMode']))
                            ? (
                              <Form.Item name={[name, 'afterSubmitTarget']} label="Target" style={{ marginBottom: 8 }}
                                rules={[{ pattern: /^\//, message: 'Must start with /' }]}>
                                <Input placeholder="/jobs/:id/source" className="fg-mono-input" style={{ width: 200 }} />
                              </Form.Item>
                            ) : null)}
                        </Form.Item>
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} style={{ marginTop: 28 }} />
                      </Space>
                      <Space wrap align="start">
                        <Form.Item name={[name, 'enabledWhenField']} label="Enable when field" style={{ marginBottom: 0 }}
                          tooltip="Keep the button disabled until this record field satisfies the condition (e.g. Assign To is set). Blank = always enabled.">
                          <Input placeholder="e.g. assignedTo" style={{ width: 200 }} />
                        </Form.Item>
                        <Form.Item name={[name, 'enabledWhenOperator']} label="Condition" style={{ marginBottom: 0 }}>
                          <Select style={{ width: 130 }} options={[
                            { value: 'notEmpty', label: 'is set' },
                            { value: 'isEmpty', label: 'is empty' },
                            { value: 'eq', label: 'equals' },
                            { value: 'neq', label: 'not equals' },
                            { value: 'in', label: 'in list' },
                            { value: 'notIn', label: 'not in list' },
                          ]} />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => (['eq', 'neq', 'in', 'notIn'].includes(getFieldValue(['formActions', name, 'enabledWhenOperator']))
                            ? (
                              <Form.Item name={[name, 'enabledWhenValue']} label="Value" style={{ marginBottom: 0 }}>
                                <Input placeholder="Value(s)" style={{ width: 160 }} />
                              </Form.Item>
                            ) : null)}
                        </Form.Item>
                      </Space>
                    </div>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ tone: 'primary', afterSubmitMode: 'stay', enabledWhenOperator: 'notEmpty' })}>
                    Add submit button
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>

          <div className="fg-inline-row">
            <Form.Item name="submitMsgAddSuccess" label="Add Success Message"
              tooltip={'Toast shown after a successful Add. ${module} is replaced with the module name. Blank = default "<Module> has added successfully".'}>
              <Input placeholder="${module} has added successfully" style={{ width: 280 }} />
            </Form.Item>
            <Form.Item name="submitMsgEditSuccess" label="Edit Success Message"
              tooltip={'Toast shown after a successful Edit. ${module} is replaced with the module name. Blank = default "<Module> has Updated successfully".'}>
              <Input placeholder="${module} has Updated successfully" style={{ width: 280 }} />
            </Form.Item>
          </div>
          <div className="fg-inline-row">
            <Form.Item name="submitMsgAddError" label="Add Error Message"
              tooltip="Toast shown when Add fails. Blank = show the server's error.">
              <Input placeholder="Server error (default)" style={{ width: 280 }} />
            </Form.Item>
            <Form.Item name="submitMsgEditError" label="Edit Error Message"
              tooltip="Toast shown when Edit fails. Blank = show the server's error.">
              <Input placeholder="Server error (default)" style={{ width: 280 }} />
            </Form.Item>
            <Form.Item name="submitMsgSuppress" label="Hide Success Toast" valuePropName="checked"
              tooltip="Prevent the success message for this module's Add/Edit (errors always show).">
              <Switch checkedChildren="Hidden" unCheckedChildren="Shown" />
            </Form.Item>
          </div>
        </div>
        <div className="fg-editor-next-row">
          <Text type="secondary">Next, review the fields included in this group.</Text>
          <Button type="primary" onClick={() => selectEditorTab('fields')}>Continue to Fields</Button>
        </div>
        </div>

        {fieldsVisited && (
        <div className={`fg-group-editor-panel${editorTab === 'fields' ? ' is-active' : ''}`}>
        <div className="fg-fields-header">
          <span className="fg-fields-title">Fields</span>
          <span className="fg-fields-hint">Open only the field you want to change</span>
          {fieldsLoading && <Spin size="small" />}
          {!fieldsLoading && options.length > 0 && (
            <span className="fg-fields-hint">{options.length} fields available</span>
          )}
        </div>
        <Form.List name="fields">
          {(fieldItems, { add, remove, move }) => renderFieldRows({
            form, fieldItems, add, remove, move,
            absBase: [],
            moduleFieldOptions: options,
            fieldsLoading, dragIndexRef, dragOverIndex, setDragOverIndex,
            onFieldKeySelect: (val, rowName) => handleFieldKeySelect(val, rowName),
            expandedFieldKeys,
            onToggleField: toggleField,
            defaultFieldSet,
          })}
        </Form.List>
        </div>
        )}
      </Form>
    </Modal>
  );
}

// ── Groups Table ──────────────────────────────────────────────────────────────
function ModuleGroupsTable({ groups, onEdit, onDelete, onCopy, onPaste, canPaste, onReorder }) {
  const dragFrom = useRef(null);
  const sorted = [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const reorderable = typeof onReorder === 'function' && sorted.length > 1;

  const handleMove = (from, to) => {
    const next = [...sorted];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  // Native-drag table row (same HTML5-drag approach as the field editor).
  // Defined here as a closure so it reads dragFrom/handleMove directly instead
  // of mutating a value passed through props.
  const DraggableGroupRow = useMemo(() => {
    function Row({ 'data-row-index': rowIndex, ...rest }) {
      if (rowIndex === undefined) return <tr {...rest} />;
      return (
        <tr
          {...rest}
          draggable
          onDragStart={() => { dragFrom.current = rowIndex; }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            const from = dragFrom.current;
            dragFrom.current = null;
            if (from !== null && from !== rowIndex) handleMove(from, rowIndex);
          }}
          style={{ ...(rest.style || {}), cursor: 'grab' }}
        />
      );
    }
    return Row;
    // handleMove closes over `sorted`/`onReorder`; recompute when the row set changes.
  }, [sorted, onReorder]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    ...(reorderable ? [{
      title: '', key: 'drag', width: 34, align: 'center',
      render: () => <HolderOutlined className="fg-drag-handle" style={{ cursor: 'grab', color: 'var(--color-text-secondary)' }} />,
    }] : []),
    {
      title: 'Label', dataIndex: 'label', key: 'label',
      render: (label, record) => (
        <Space size={6}>
          <Text strong style={{ color: 'var(--color-text-heading)' }}>{label}</Text>
          {record.hideGroup && <Tag color="default">Hidden</Tag>}
          {record.moduleName && <Tag color="purple">→ {record.moduleName}</Tag>}
        </Space>
      ),
    },
    { title: 'Key', dataIndex: 'name', key: 'name', render: (name) => <code className="fg-table-code">{name}</code> },
    {
      title: 'Add Row', dataIndex: 'addRow', key: 'addRow', align: 'center', width: 90,
      render: (val) => val
        ? <Tag color="blue" style={{ margin: 0 }}>Yes</Tag>
        : <Tag color="default" style={{ margin: 0 }}>No</Tag>,
    },
    {
      title: 'Order', dataIndex: 'order', key: 'order', align: 'center', width: 72,
      render: (v) => <span className="fg-order-cell">{v ?? 0}</span>,
    },
    {
      title: 'Fields', dataIndex: 'fields', key: 'fields', align: 'center', width: 76,
      render: (fields) => <Badge count={fields?.length ?? 0} showZero color="var(--color-brand)" />,
    },
    {
      title: '', key: 'actions', align: 'right', width: 132,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Copy settings">
            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => onCopy(record)} className="fg-action-btn" />
          </Tooltip>
          <Tooltip title="Paste copied settings into this group">
            <Button type="text" size="small" icon={<SnippetsOutlined />} disabled={!canPaste} onClick={() => onPaste(record)} className="fg-action-btn" />
          </Tooltip>
          <Tooltip title="Edit group">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} className="fg-action-btn" />
          </Tooltip>
          <Popconfirm
            title="Delete this group?"
            description="All field mappings inside will be removed."
            onConfirm={() => onDelete(record.id)}
            okText="Delete" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete group">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} className="fg-action-btn" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      rowKey={(r) => r.id ?? r.name}
      dataSource={sorted}
      columns={columns}
      pagination={false}
      className="fg-table"
      components={reorderable ? { body: { row: DraggableGroupRow } } : undefined}
      onRow={reorderable
        ? (_, index) => ({ 'data-row-index': index, dragCtx: { from: dragFrom, onMove: handleMove } })
        : undefined}
      locale={{
        emptyText: (
          <Empty description="No groups configured for this module yet."
            image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
        ),
      }}
      expandable={{
        expandedRowRender: (record) =>
          (record.fields ?? []).length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>No fields in this group.</Text>
          ) : (
            <div className="fg-expanded-fields">
              {(record.fields ?? []).map((f) => {
                const dsColorMap = { master: 'geekblue', module: 'cyan', lookup: 'volcano', static: 'default' };
                const dsLabelMap = {
                  master: `master: ${f.masterName || '?'}`,
                  module: 'module',
                  lookup: `lookup: ${f.lookupCollection || '?'} → ${f.displayField || '?'}`,
                  static: `static (${(f.options ?? []).length})`,
                };
                return (
                  <div key={f.field} className="fg-field-row">
                    <div className="fg-field-row-info">
                      <span className="fg-field-row-label">{f.label}</span>
                      <span className="fg-field-row-key">{f.field}</span>
                      <span className="fg-field-row-type">({f.type})</span>
                      {['select', 'radio', 'checkbox'].includes(f.type) && f.dataSource && (
                        <Tag color={dsColorMap[f.dataSource] ?? 'default'} style={{ fontSize: 10, marginLeft: 6 }}>
                          {dsLabelMap[f.dataSource] ?? f.dataSource}
                        </Tag>
                      )}
                    </div>
                    <div className="fg-field-row-flags">
                      {f.show === 1 && <Tag color="blue" style={{ marginRight: 4 }}>Show</Tag>}
                      {f.showOnAdd === false && <Tag color="orange" style={{ marginRight: 4 }}>Edit only</Tag>}
                      {f.req  === 1 && <Tag color="red"  style={{ marginRight: 4 }}>Required</Tag>}
                      {f.edit === 1 && <Tag color="green" style={{ marginRight: 4 }}>Editable</Tag>}
                      {f.formatter && <Tag color="purple" style={{ marginRight: 4 }}>fmt: {f.formatter}</Tag>}
                    </div>
                    {(f.validations ?? []).length > 0 && (
                      <div className="fg-field-row-validations">
                        <CheckCircleOutlined style={{ color: 'var(--color-brand)', fontSize: 11, marginRight: 4 }} />
                        {(f.validations ?? []).map((v, vi) => (
                          <Tooltip key={vi}
                            title={`${v.value !== undefined && v.value !== '' ? `Value: ${v.value} — ` : ''}${v.message}`}>
                            <Tag color={VALIDATION_TAG_COLOR[v.type] ?? 'default'}
                              style={{ cursor: 'default', fontFamily: 'monospace', fontSize: 10 }}>
                              {v.type}{v.value !== undefined && v.value !== '' ? `(${v.value})` : ''}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ),
        rowExpandable: () => true,
      }}
    />
  );
}

// ── Custom Form Drawer ────────────────────────────────────────────────────────
// Full drawer: form identity (module/action/label) + groups with fields.
// Each group's field list uses renderFieldRows with absBase=['groups', gName].
function CustomFormDrawer({ open, editingForm, moduleOptions, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [moduleFields, setModuleFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const loadedRef = useRef({});
  const dragIndexRef = useRef(null);

  async function ensureModuleFields(mod) {
    if (!mod || loadedRef.current[mod]) return;
    setFieldsLoading(true);
    try {
      const fields = await getModuleFields(mod);
      setModuleFields((prev) => ({ ...prev, [mod]: fields }));
      loadedRef.current[mod] = true;
    } catch { /* fall back to free-text */ } finally {
      setFieldsLoading(false);
    }
  }

  useEffect(() => {
    if (!open) { loadedRef.current = {}; setModuleFields({}); return; }
    if (editingForm) {
      const mod = editingForm.module;
      setSelectedModule(mod);
      form.setFieldsValue({
        module: mod,
        formType: editingForm.formType || "popup",
        action: editingForm.action,
        label:  editingForm.label,
        groups: (editingForm.groups ?? []).map((g) => ({
          ...g,
          hideGroup: g.hideGroup ?? false,
          moduleName: g.moduleName ?? '',
          infoEnabled: g.infoEnabled ?? false,
          infoText: g.infoText ?? '',
          minRows: g.minRows ?? '',
          initialRows: g.initialRows ?? '',
          requireOne: g.requireOne ?? false,
          linkGroup: g.linkGroup ?? '',
          toggleEnabled: g.toggleEnabled ?? false,
          toggleLabel: g.toggleLabel ?? '',
          toggleField: g.toggleField ?? '',
          fields: (g.fields ?? []).map(initFieldValues),
        })),
      });
      ensureModuleFields(mod);
    } else {
      form.resetFields();
      form.setFieldsValue({ groups: [], formType: "popup" });
      setSelectedModule('');
    }
  }, [open, editingForm, form]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleModuleChange(mod) { setSelectedModule(mod); ensureModuleFields(mod); }

  function fieldOptions() {
    return (moduleFields[selectedModule] ?? []).map((f) => ({
      value: f.value,
      label: (
        <span>{f.label}<span className="fg-field-key-hint">{f.value}</span></span>
      ),
      searchLabel: `${f.label} ${f.value}`.toLowerCase(),
    }));
  }

  async function handleSave() {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        module: values.module,
        formType: values.formType,
        action: values.action.trim(),
        label:  values.label,
        groups: (values.groups ?? []).map((g) => ({
          label:  g.label,
          name:   g.name,
          addRow: g.addRow ?? false,
          ...(g.hideGroup ? { hideGroup: true } : {}),
          ...(g.moduleName ? { moduleName: g.moduleName } : {}),
          // Preserve dependency config (edited in the main group editor) so a
          // custom-form re-save never strips it.
          ...(g.moduleName && (g.writeRefs ?? []).length ? { writeRefs: g.writeRefs } : {}),
          ...(g.moduleName && (g.dependsOn ?? []).length ? { dependsOn: g.dependsOn } : {}),
          ...(g.moduleName && g.optionalWrite ? { optionalWrite: true } : {}),
          // Preserve the Target Collection linkage (child→parent linkField or
          // forward-lookup parentRefField) so a custom-form re-save never strips it.
          ...(g.moduleName && g.linkField ? { linkField: g.linkField } : {}),
          ...(g.moduleName && g.linkTypeField ? { linkTypeField: g.linkTypeField } : {}),
          ...(g.moduleName && g.parentRefField ? { parentRefField: g.parentRefField } : {}),
          // Preserve append-history config the same way — independent of
          // Target Collection, so it's guarded by its own field, not moduleName.
          ...(g.appendHistory && g.historyField ? {
            appendHistory: true,
            historyField: g.historyField,
            ...(g.historyTimestampField ? { historyTimestampField: g.historyTimestampField } : {}),
          } : {}),
          ...(g.addRow && g.minRows !== undefined && g.minRows !== '' ? { minRows: Number(g.minRows) } : {}),
          ...(g.addRow && g.initialRows !== undefined && g.initialRows !== '' ? { initialRows: Number(g.initialRows) } : {}),
          ...(g.addRow && g.requireOne ? { requireOne: true } : {}),
          ...(g.addRow && g.linkGroup ? { linkGroup: g.linkGroup.trim() } : {}),
          order:  Number(g.order ?? 0),
          fields: buildFieldPayload(g.fields ?? []),
          ...(g.infoEnabled && g.infoText ? { infoEnabled: true, infoText: g.infoText } : {}),
        })),
      };
      if (editingForm) {
        await updateCustomForm(editingForm.id, { ...editingForm, ...payload });
        message.success('Custom form updated');
      } else {
        await createCustomForm(payload);
        message.success('Custom form created');
      }
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not save custom form');
    } finally { setSaving(false); }
  }

  const fldOptions = fieldOptions();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1120}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal"
      title={
        <span className="fg-drawer-title">
          {editingForm ? <EditOutlined /> : <PlusOutlined />}
          {editingForm ? ' Edit Custom Form' : ' New Custom Form'}
        </span>
      }
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}
            icon={editingForm ? <EditOutlined /> : <PlusOutlined />}>
            {editingForm ? 'Update' : 'Create'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" className="fg-form fg-editor-form">
        {/* Identity */}
        <div className="fg-form-section fg-editor-sticky-section">
          <div className="fg-form-section-label">Form Identity</div>
          <Form.Item name="module" label="Module" rules={[{ required: true, message: 'Select a module' }]}>
            <Select options={moduleOptions} placeholder="Select module" onChange={handleModuleChange} />
          </Form.Item>
          <Form.Item name="formType" label="Form Type" rules={[{ required: true, message: 'Form Type is required' }]}
            initialValue="popup">
            <Select placeholder="Select form type"
              options={[
                { label: 'Search', value: 'search' },
                { label: 'Popup', value: 'popup' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="action"
            label="Action Key"
            extra="Unique identifier used as ?action= in the URL (e.g. quickAdd, popup)"
            rules={[
              { required: true, message: 'Action is required' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Letters, numbers, hyphens, underscores only' },
            ]}
          >
            <Input placeholder="e.g. quickAdd" className="fg-mono-input" disabled={!!editingForm} />
          </Form.Item>
          <Form.Item name="label" label="Display Label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input placeholder="e.g. Quick Add Job" />
          </Form.Item>
        </div>

        {/* Groups */}
        <div className="fg-fields-header">
          <span className="fg-fields-title">Groups</span>
          <span className="fg-fields-hint">Each group holds a set of form fields</span>
          {fieldsLoading && <Spin size="small" />}
        </div>

        <Form.List name="groups">
          {(groupItems, { add: addGroup, remove: removeGroup }) => (
            <>
              {groupItems.map(({ key: gKey, name: gName }) => (
                <div key={gKey} className="fg-form-section" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span className="fg-form-section-label" style={{ marginBottom: 0 }}>Group {gName + 1}</span>
                    <Tooltip title="Remove group">
                      <button type="button" className="fg-field-remove-btn"
                        onClick={() => removeGroup(gName)} aria-label="Remove group">
                        <MinusCircleOutlined />
                      </button>
                    </Tooltip>
                  </div>

                  <div className="fg-inline-row" style={{ flexWrap: 'wrap', marginBottom: 8 }}>
                    <Form.Item name={[gName, 'label']} label="Group Label"
                      rules={[{ required: true, message: 'Required' }]}
                      style={{ flex: 2, marginBottom: 8 }}>
                      <Input placeholder="e.g. Basic Info" />
                    </Form.Item>
                    <Form.Item name={[gName, 'name']} label="Group Key"
                      rules={[
                        { required: true, message: 'Required' },
                        { pattern: /^[a-z0-9_]+$/, message: 'Lowercase, numbers, underscores' },
                      ]}
                      style={{ flex: 2, marginBottom: 8 }}>
                      <Input placeholder="e.g. basicInfo" className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[gName, 'addRow']} label="Add Row" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[gName, 'hideGroup']} label="Hide Group" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}
                      tooltip="Hide this group entirely from Add/Edit forms — not rendered, not validated, not submitted.">
                      <Switch size="small" checkedChildren="Hidden" unCheckedChildren="Visible" />
                    </Form.Item>
                    <Form.Item name={[gName, 'moduleName']} label="Target Collection"
                      style={{ marginBottom: 8, flex: 2 }}
                      tooltip="Route THIS group's fields to a different collection instead of the default module record. Leave blank for default behaviour.">
                      <AutoComplete
                        size="small"
                        options={moduleOptions.map((m) => ({ value: m.value, label: m.label }))}
                        placeholder="Default"
                        allowClear
                        filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                        className="fg-mono-input"
                      />
                    </Form.Item>
                    <Form.Item name={[gName, 'minRows']} label="Min Rows"
                      style={{ marginBottom: 8 }}
                      tooltip="Minimum rows users must keep. Delete is disabled at this count.">
                      <Input type="number" min={0} style={{ width: 80 }} />
                    </Form.Item>
                    <Form.Item name={[gName, 'initialRows']} label="Initial Rows"
                      style={{ marginBottom: 8 }}
                      tooltip="How many empty rows to show on load (0 = show only the + button).">
                      <Input type="number" min={0} style={{ width: 80 }} />
                    </Form.Item>
                    <Form.Item name={[gName, 'requireOne']} label="Row Required" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}
                      tooltip="When users add a row, at least one field in that row must be filled.">
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[gName, 'linkGroup']} label="Link Group"
                      style={{ marginBottom: 8 }}
                      tooltip="Groups sharing the same value here stay row-synced — adding or removing a row in one adds/removes the matching row in every linked group, while each keeps its own card and its own submitted data. Leave blank for an independent group.">
                      <Input size="small" placeholder="e.g. workHistory" style={{ width: 130 }} className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[gName, 'infoEnabled']} label="Group Info" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="Info" unCheckedChildren="Info" />
                    </Form.Item>
                    <Form.Item name={[gName, 'toggleEnabled']} label="Group Switch" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                    <Form.Item name={[gName, 'order']} label="Order" initialValue={0}
                      style={{ marginBottom: 8 }}>
                      <Input type="number" min={0} style={{ width: 70 }} />
                    </Form.Item>
                  </div>
                  <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                    {({ getFieldValue }) => getFieldValue(['groups', gName, 'infoEnabled']) ? (
                      <Form.Item
                        name={[gName, 'infoText']}
                        label="Group Info Message"
                        rules={[{ required: true, message: 'Enter group info message' }]}
                      >
                        <Input.TextArea rows={2} placeholder="Message shown when users hover the group info icon" />
                      </Form.Item>
                    ) : null}
                  </Form.Item>
                  <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                    {({ getFieldValue }) => getFieldValue(['groups', gName, 'toggleEnabled']) ? (
                      <div className="fg-inline-row">
                        <Form.Item
                          name={[gName, 'toggleLabel']}
                          label="Switch Label"
                          rules={[{ required: true, message: 'Enter switch label' }]}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <Input placeholder="e.g. Fresher" />
                        </Form.Item>
                        <Form.Item
                          name={[gName, 'toggleField']}
                          label="Payload Field"
                          rules={[
                            { required: true, message: 'Enter payload field' },
                            { pattern: /^[A-Za-z_][\w.]*$/, message: 'Use a valid key, dot path allowed' },
                          ]}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <Input placeholder="e.g. isFresher" className="fg-mono-input" />
                        </Form.Item>
                      </div>
                    ) : null}
                  </Form.Item>

                  <div className="fg-fields-header" style={{ marginTop: 4, marginBottom: 8 }}>
                    <span className="fg-fields-title" style={{ fontSize: 12 }}>Fields</span>
                  </div>

                  <Form.List name={[gName, 'fields']}>
                    {(fieldItems, { add, remove, move }) => renderFieldRows({
                      form, fieldItems, add, remove, move,
                      absBase: ['groups', gName],
                      moduleFieldOptions: fldOptions,
                      fieldsLoading, dragIndexRef, dragOverIndex, setDragOverIndex,
                      onFieldKeySelect: (val, rowName) => {
                        const apiFields = moduleFields[selectedModule] ?? [];
                        const match = apiFields.find((f) => f.value === val);
                        if (!match) return;
                        const current = form.getFieldValue(['groups', gName, 'fields', rowName]) ?? {};
                        form.setFieldValue(['groups', gName, 'fields', rowName], {
                          ...current, field: match.value, label: match.label, type: match.type ?? 'text',
                        });
                      },
                    })}
                  </Form.List>
                </div>
              ))}

              <Button
                type="dashed" icon={<PlusOutlined />} block style={{ marginBottom: 16 }}
                onClick={() => addGroup({ label: '', name: '', addRow: false, order: groupItems.length, fields: [] })}
              >
                Add Group
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}

// ── Custom Forms Section ──────────────────────────────────────────────────────
function CustomFormsSection({ moduleOptions }) {
  const [customForms, setCustomForms] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editingForm, setEditingForm] = useState(null);

  function loadForms() {
    setLoading(true);
    getCustomForms()
      .then((data) => setCustomForms(data))
      .catch((err) => message.error(err.message || 'Could not load custom forms'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadForms(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(record) {
    try {
      await deleteCustomForm(record.id);
      message.success('Custom form deleted');
      loadForms();
    } catch (err) {
      message.error(err.message || 'Could not delete custom form');
    }
  }

  const columns = [
    {
      title: 'Module', dataIndex: 'module', key: 'module',
      render: (mod) => <Tag color="blue">{mod}</Tag>,
    },
    {
      title: 'Action Key', dataIndex: 'action', key: 'action',
      render: (a) => <code className="fg-table-code">{a}</code>,
    },
    {
      title: 'Label', dataIndex: 'label', key: 'label',
      render: (lbl) => <Text strong style={{ color: 'var(--color-text-heading)' }}>{lbl}</Text>,
    },
    {
      title: 'Groups', dataIndex: 'groups', key: 'groups', align: 'center', width: 76,
      render: (groups) => <Badge count={groups?.length ?? 0} showZero color="var(--color-brand)" />,
    },
    {
      title: '', key: 'actions', align: 'right', width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit form">
            <Button type="text" size="small" icon={<EditOutlined />}
              onClick={() => { setEditingForm(record); setDrawerOpen(true); }}
              className="fg-action-btn" />
          </Tooltip>
          <Popconfirm
            title="Delete this custom form?"
            description="All configured groups will be lost."
            onConfirm={() => handleDelete(record)}
            okText="Delete" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete form">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} className="fg-action-btn" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="fg-tab-toolbar" style={{ marginBottom: 12 }}>
        <Button type="primary" size="small" icon={<PlusOutlined />}
          onClick={() => { setEditingForm(null); setDrawerOpen(true); }}>
          New Custom Form
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey={(r) => r.id ?? r.action}
          dataSource={customForms}
          columns={columns}
          pagination={false}
          className="fg-table"
          locale={{
            emptyText: (
              <Empty description="No custom forms yet. Click 'New Custom Form' to create one."
                image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
            ),
          }}
        />
      </Spin>

      <CustomFormDrawer
        open={drawerOpen}
        editingForm={editingForm}
        moduleOptions={moduleOptions}
        onClose={() => { setDrawerOpen(false); setEditingForm(null); }}
        onSaved={() => { setDrawerOpen(false); setEditingForm(null); loadForms(); }}
      />
    </div>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
export default function FormGroupsSection() {
  const [apiModules, setApiModules]         = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [moduleState, setModuleState]       = useState({});
  const [activeModule, setActiveModule]     = useState('all');
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [editingGroup, setEditingGroup]     = useState(null);
  const [gate, setGate]                     = useState(null); // { action, title, confirmLabel, danger, description }
  const [topTab, setTopTab]                 = useState('module');
  const [clients, setClients]               = useState([]);
  const [scope, setScope]                   = useState({ clientId: '', region: '' });
  const [previewOpen, setPreviewOpen]       = useState(false);
  const [copiedGroup, setCopiedGroup]       = useState(null);

  const tabs          = buildModuleTabs(apiModules);
  const moduleOptions = apiModules.map((m) => ({ value: m.key, label: m.label }));

  useEffect(() => {
    getAvailableModules()
      .then((raw) => {
        const mods = normalizeModuleOptions(raw).map(({ value, label }) => ({ key: value, label }));
        setApiModules(mods);
        const built = buildModuleTabs(mods);
        setModuleState(initModuleState(built));
        loadModuleInto('all', built);
      })
      .catch(() => setApiModules([]))
      .finally(() => setModulesLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getAdminClients({ limit: 200 })
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  function patchModule(key, patch) {
    setModuleState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  function loadModuleInto(key, tabList) {
    const currentTabs = tabList ?? tabs;
    if (!currentTabs.find((t) => t.key === key)) return;
    patchModule(key, { loading: true, error: '' });
    const requestScope = key === 'jobs' ? scope : {};
    getAdminFormGroups(key === 'all' ? '' : key, requestScope)
      .then((data) => patchModule(key, { groups: data, loading: false, loaded: true }))
      .catch((err) => patchModule(key, { error: err.message || 'Could not load groups', loading: false }));
  }

  function loadModule(key) { loadModuleInto(key, null); }
  function handleTabChange(key) {
    setActiveModule(key);
    if (!moduleState[key]?.loaded) loadModule(key);
  }
  function openCreate() { setEditingGroup(null); setDrawerOpen(true); }
  function openEdit(group) { setEditingGroup(group); setDrawerOpen(true); }
  function closeDrawer() { setDrawerOpen(false); setEditingGroup(null); }

  async function handleSaved() {
    closeDrawer();
    loadModule(activeModule);
    if (activeModule !== 'all') loadModule('all');
  }

  async function handleDelete(id) {
    try {
      await deleteAdminFormGroup(id);
      message.success('Group deleted');
      loadModule(activeModule);
      if (activeModule !== 'all') loadModule('all');
    } catch (err) {
      message.error(err.message || 'Could not delete group');
    }
  }

  // Persist a drag-and-drop group reorder: renumber each group's `order` from
  // its new position and PUT only the ones that actually moved, so the rendered
  // form (which sorts groups by `order`) matches the admin's arrangement.
  async function handleReorderGroups(reordered) {
    const changed = reordered
      .map((g, index) => ({ g, index }))
      .filter(({ g, index }) => Number(g.order ?? 0) !== index && g.id);
    if (!changed.length) return;
    try {
      await Promise.all(changed.map(({ g, index }) =>
        updateAdminFormGroup(g.id, { ...g, order: index }, activeScopeForSave(g.module || activeModule))));
      message.success('Group order updated');
      loadModule(activeModule);
      if (activeModule !== 'all') loadModule('all');
    } catch (err) {
      message.error(err.message || 'Could not reorder groups');
      loadModule(activeModule); // reload to discard the optimistic order
    }
  }

  function openGate(action) { setGate(CONFIG_GATE_META[action]); }

  function handleScopeChange(next) {
    const merged = { ...scope, ...next };
    setScope(merged);
    if (activeModule === 'jobs') {
      patchModule('jobs', { loading: true, error: '' });
      getAdminFormGroups('jobs', merged)
        .then((data) => patchModule('jobs', { groups: data, loading: false }))
        .catch((err) => patchModule('jobs', { error: err.message || 'Could not load scoped groups', loading: false }));
    }
  }

  function activeScopeForSave(moduleKey = activeModule) {
    return moduleKey === 'jobs' ? scope : {};
  }

  function copyGroupSettings(group) {
    setCopiedGroup(JSON.parse(JSON.stringify(group)));
    message.success(`Copied settings from ${group.label || group.name}`);
  }

  async function pasteGroupSettings(target) {
    if (!copiedGroup) return;
    try {
      const payload = {
        ...target,
        addRow: copiedGroup.addRow ?? target.addRow,
        payloadKey: copiedGroup.payloadKey ?? target.payloadKey,
        fields: JSON.parse(JSON.stringify(copiedGroup.fields ?? [])),
      };
      await updateAdminFormGroup(target.id, payload, activeScopeForSave(target.module || activeModule));
      message.success(`Pasted settings into ${target.label || target.name}`);
      loadModule(activeModule);
      if (activeModule !== 'all') loadModule('all');
    } catch (err) {
      message.error(err.message || 'Could not paste settings');
    }
  }

  // Called after the OTP modal verifies + performs the action — reload so the
  // table reflects the new (reset / restored / unchanged) config.
  function handleGateVerified() {
    setGate(null);
    setModuleState(initModuleState(tabs));
    loadModule(activeModule);
    if (activeModule !== 'all') loadModule('all');
  }

  const tabItems = tabs.map(({ key, label, color }) => {
    const state  = moduleState[key] ?? { groups: [], loading: false, loaded: false, error: '' };
    const sorted = [...state.groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return {
      key,
      label: (
        <span className="fg-tab-label">
          <span className="fg-tab-dot" style={{ background: color }} />
          {label}
          <Badge count={state.groups.length} showZero color={color} size="small" />
        </span>
      ),
      children: (
        <div className="fg-tab-content">
          {state.error && <Alert type="error" showIcon message={state.error} style={{ marginBottom: 12 }} />}
          <div className="fg-tab-toolbar">
            <Button type="primary" size="small" icon={<PlusOutlined />}
              disabled={key === 'all'}
              onClick={() => { setActiveModule(key); openCreate(); }}>
              Add Group
            </Button>
          </div>
          <Spin spinning={state.loading}>
            <ModuleGroupsTable
              groups={sorted}
              onEdit={openEdit}
              onDelete={handleDelete}
              onCopy={copyGroupSettings}
              onPaste={pasteGroupSettings}
              canPaste={!!copiedGroup}
              onReorder={key === 'all' ? undefined : handleReorderGroups}
            />
          </Spin>
        </div>
      ),
    };
  });

  const topTabItems = [
    {
      key: 'module',
      label: 'Module Form',
      children: (
        <>
          <AppCard>
            <div className="fg-scope-toolbar">
              <div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Select a client to edit a scoped Jobs field list. Leave blank for the default module config.
                  </Text>
                </div>
              </div>
              <Space wrap size={8}>
                <Select
                  size="small"
                  allowClear
                  showSearch
                  placeholder="Client"
                  style={{ width: 240 }}
                  value={scope.clientId || undefined}
                  optionFilterProp="label"
                  options={clients.map((c) => ({
                    value: c._id ?? c.id,
                    label: c.clientName ?? c.name ?? c.clientRefrenceId ?? c._id,
                  })).filter((c) => c.value)}
                  onChange={(clientId) => handleScopeChange({ clientId: clientId ?? '' })}
                />
                <Button size="small" icon={<ReloadOutlined />} onClick={() => loadModule(activeModule)}>
                  Reload
                </Button>
                <Button size="small" icon={<EyeOutlined />} disabled={activeModule !== 'jobs'} onClick={() => setPreviewOpen(true)}>
                  Preview
                </Button>
              </Space>
            </div>
          </AppCard>

          <AppCard>
            <Spin spinning={modulesLoading}>
              <Tabs
                activeKey={activeModule}
                onChange={handleTabChange}
                items={tabItems}
                size="middle"
                className="fg-tabs"
              />
            </Spin>
          </AppCard>

          <GroupDrawer
            open={drawerOpen}
            editingGroup={editingGroup}
            defaultModule={activeModule === 'all' ? (moduleOptions[0]?.value ?? '') : activeModule}
            moduleOptions={moduleOptions}
            allGroupsByModule={Object.fromEntries(
              Object.entries(moduleState)
                .filter(([k]) => k !== 'all')
                .map(([k, v]) => [k, v.groups ?? []])
            )}
            onClose={closeDrawer}
            onSaved={handleSaved}
            onSaveGroup={(payload, group) => group
              ? updateAdminFormGroup(group.id, payload, activeScopeForSave(payload.module))
              : createAdminFormGroup(payload, activeScopeForSave(payload.module))}
          />

          <Drawer
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            width="92vw"
            title={<span className="fg-drawer-title"><EyeOutlined /> Jobs Add Form Preview</span>}
            classNames={{ body: 'fg-preview-body' }}
          >
            <AddFormV1
              moduleName="jobs"
              clientId={scope.clientId}
              region={scope.region}
              cancelPath="/jobs"
              onSubmit={async () => message.info('Preview only. No record was created.')}
              breadcrumbItems={[
                { label: 'Admin' },
                { label: 'Form Groups' },
                { label: 'Jobs Preview' },
              ]}
            />
          </Drawer>
        </>
      ),
    },
    {
      key: 'custom',
      label: 'Custom Form',
      children: (
        <AppCard>
          <CustomFormsSection moduleOptions={moduleOptions} />
        </AppCard>
      ),
    },
    {
      key: 'defaultFields',
      label: 'Default Fields',
      children: <DefaultFieldsPanel moduleOptions={moduleOptions} />,
    },
  ];

  return (
    <div className="fg-section">
      <AppCard
        icon={<AppstoreAddOutlined />}
        title="Form Groups"
        extra={
          topTab === 'module' ? (
            <Space size={8} wrap>
              <Dropdown trigger={['click']} menu={{ items: [
                { key: 'save', label: 'Save current as default', icon: <SaveOutlined />, onClick: () => openGate('save') },
                { key: 'restore', label: 'Restore saved default', icon: <CloudDownloadOutlined />, onClick: () => openGate('restore') },
                { type: 'divider' },
                { key: 'reset', label: 'Reset all form groups', icon: <ReloadOutlined />, danger: true, onClick: () => openGate('reset') },
              ] }}>
                <Button size="small" icon={<MoreOutlined />}>Workspace defaults</Button>
              </Dropdown>
              <Button type="primary" size="small" icon={<AppstoreAddOutlined />} onClick={openCreate}>
                New Group
              </Button>
            </Space>
          ) : null
        }
      >
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          <strong>Module Form</strong> — configure the default add/edit form for each module.{' '}
          <strong>Custom Form</strong> — create standalone named forms (module + action) for
          context-specific flows such as quick-add or popup forms.
        </Text>
      </AppCard>

      <AppCard styles={{ body: { padding: '0 0' } }}>
        <Tabs
          activeKey={topTab}
          onChange={setTopTab}
          items={topTabItems}
          size="middle"
          className="fg-top-tabs"
          tabBarStyle={{ padding: '0 16px', margin: 0 }}
          destroyInactiveTabPane={false}
        />
      </AppCard>

      <OtpGateModal
        open={!!gate}
        scope="formGroups"
        action={gate?.action}
        title={gate?.title}
        description={gate?.description}
        confirmLabel={gate?.confirmLabel}
        danger={gate?.danger}
        onCancel={() => setGate(null)}
        onVerified={handleGateVerified}
      />
    </div>
  );
}
